"use client";

import {
  memo,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Plus, Mic, ChevronRight, ChevronsLeft, FileText, Settings, CheckSquare,
  User, Users2, Trash2, LogOut, ChevronDown, Edit3, X, Calendar, Search,
  Handshake, Crown, Zap, VolumeX, ArrowLeft, LayoutDashboard, BarChart3,
  ListTodo, ListFilter, HelpCircle, Menu, BadgeDollarSign, Stethoscope,
  Rocket, Lock, Loader2,
} from "lucide-react";
import logo from "../../asset/VirtualScribe_logo.png";
import { Button } from "../ui/button";
import { createPortal } from "react-dom";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { sessionService } from "../../apis/sessionService";
import {
  getSessionsCacheKey as buildSessionsCacheKey,
  readSessionsListCache,
  filterVisibleSessions,
  removeSessionFromListCache,
  sortSessionsNewestFirst,
} from "../../utils/sessionsListCache";
import {
  isDemoSession,
  mergeDemoSessionIntoList,
} from "../../utils/demoVisitStore";
import { useAuth } from "../../context/AuthContext";
import { useTrial } from "../../context/TrialContext";
import { toast } from "react-hot-toast";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { PopoverArrow } from "@radix-ui/react-popover";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import { Calendar as CalendarComponent } from "../ui/calendar";
import TaskPage from "../../components/task-settings";
import { SettingsHeader } from "../../components/settings-header.jsx";
import { ScribeDashboard } from "../scribe-tasks/ScribeDashboard.jsx";
import VBotSubscriptionModal from "../ui/vbot-subscription-modal.jsx";
import { useAudioCache, prefetchUserAudioFiles } from "../../hooks/useAudioCache.jsx";
import AudioPlaybackControls from "./audio-playback-controls-setting.jsx";
import CorsInfo from "./cors-info";
import AccessibilityWidget from "../ui/AccessibilityWidget.jsx";
import {
  ContextMenu, useContextMenu, buildNavContextItems,
} from "../ui/ContextMenu";
import { adminService } from "../../apis/adminService.js";
import { useNotifications } from "../../context/NotificationContext";
import { aiService } from "../../apis/aiService";
import { TEMPLATES_FEATURE_ENABLED } from "../../context/FeatureFlagsContext";
import { cleanSessionName } from '../../utils/sessionUtils';
import { goToTalkToSales } from "../../constants/publicSite";
import {
  listParkedCaptureSessionIds,
  subscribeParkedCaptures,
} from "../../utils/parkedCaptureStore";
import { useSessionTranscriptionProgress } from "../../hooks/useTranscriptionJobProgress";
import { applyLocalSessionProgress } from "../../utils/transcriptionJobProgressStore";
import { visitTypeApiService } from "../../apis/visitTypeService";

import { useSignedUrlRetry } from "../../hooks/useSignedUrlRetry";
import { toAbsoluteAvatarUrl, fetchAuthMeAvatarUrl } from "../../utils/avatarUrl";

// ─────────────────────────────────────────────────────────────────
// STABLE TOP-LEVEL HELPERS / COMPONENTS (moved out of SessionManager)
// ─────────────────────────────────────────────────────────────────
const UUID_NAME_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function FilterSelect({ value, onChange, options, placeholder, open, setOpen, onWillOpen }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const selectedLabel = options.find((opt) => opt.value === value)?.label || placeholder;

  const placeMenu = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const gap = 6;
    const spaceBelow = viewportH - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < 168 && spaceAbove > spaceBelow;
    const available = Math.max(openUp ? spaceAbove : spaceBelow, 96);
    const maxH = Math.min(Math.round(viewportH * 0.4), available - 4);
    const width = Math.min(Math.max(rect.width, 180), viewportW - 16);
    const left = Math.max(8, Math.min(rect.left, viewportW - width - 8));
    setMenuStyle({
      position: "fixed",
      left,
      width,
      zIndex: 300,
      maxHeight: Math.max(96, maxH),
      ...(openUp
        ? { bottom: viewportH - rect.top + gap, top: "auto" }
        : { top: rect.bottom + gap, bottom: "auto" }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    placeMenu();
    const onReposition = () => placeMenu();
    const onPointerDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, placeMenu, options.length, setOpen]);

  return (
    <div className="relative" ref={triggerRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (open) {
            setOpen(false);
          } else {
            onWillOpen?.();
            setOpen(true);
          }
        }}
        className="w-full h-10 text-sm bg-white border border-gray-200 rounded-lg px-2.5 py-1 flex items-center justify-between gap-1 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
      >
        <span className="truncate text-left">{selectedLabel}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && menuStyle && createPortal(
        <div
          ref={menuRef}
          className="border border-gray-200 rounded-lg shadow-lg bg-white overflow-y-auto overscroll-contain"
          style={menuStyle}
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={(e) => {
                e.stopPropagation();
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 transition ${
                value === opt.value
                  ? "bg-blue-100 text-blue-700"
                  : "text-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function getDisplayCreatorName(...values) {
  for (const value of values) {
    const label = String(value || "").trim();
    if (!label || label === "Unknown" || label === "System") continue;
    if (UUID_NAME_RE.test(label)) continue;
    return label;
  }
  return "";
}

function groupSessionsByDate(sessions) {
  const groups = {};
  sessions.forEach((session) => {
    const date = new Date(session.createdAt || session.visitDate || session.date);
    const dateKey = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric'
    });
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDay = new Date(date);
    sessionDay.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - sessionDay) / (1000 * 60 * 60 * 24));
    let displayKey = dateKey;
    if (diffDays === 0) displayKey = 'Today';
    else if (diffDays === 1) displayKey = 'Yesterday';
    if (!groups[displayKey]) groups[displayKey] = [];
    groups[displayKey].push(session);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    if (a === 'Today') return -1;
    if (b === 'Today') return 1;
    if (a === 'Yesterday') return -1;
    if (b === 'Yesterday') return 1;
    const dateA = new Date(a); const dateB = new Date(b);
    return dateB - dateA;
  });
  const result = {};
  sortedKeys.forEach(key => { result[key] = groups[key]; });
  return result;
}

const SessionSkeletonRow = () => (
  <div className="rounded-2xl p-4 border border-slate-200/80 bg-white shadow-sm">
    <div className="flex justify-between items-start gap-2 mb-3 animate-pulse">
      <div className="h-4 w-2/3 bg-slate-200 rounded-md" />
      <div className="h-4 w-4 bg-slate-200 rounded-full shrink-0" />
    </div>
    <div className="h-3 w-1/3 bg-slate-200 rounded-md mb-2 animate-pulse" />
    <div className="h-3 w-2/5 bg-slate-100 rounded-md animate-pulse" />
  </div>
);

const SessionListSkeleton = ({ count = 6 }) => (
  <div className="space-y-3 h-full" aria-busy="true" aria-label="Loading visits">
    <div className="flex items-center gap-2 px-1 py-1 text-xs font-medium text-slate-500">
      <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 shrink-0" />
      <span>Loading visits…</span>
    </div>
    {Array.from({ length: count }).map((_, i) => (
      <SessionSkeletonRow key={i} />
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────
// SearchFilterRow 
// ─────────────────────────────────────────────────────────────────
const SearchFilterRow = memo(function SearchFilterRow({
  searchTerm, setSearchTerm, filterPopoverOpen, setFilterPopoverOpen,
  filterContent, extraButton, isSyncingAudio = false,
  hasActiveFilters = false, onClearFilters,
}) {
  const showActiveIndicator = hasActiveFilters && !filterPopoverOpen;
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    if (searchTerm === "") setInputValue("");
  }, [searchTerm]);

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchTerm(value), 300);
  };

  const handleClear = useCallback(() => {
    setInputValue("");
    setSearchTerm("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    searchInputRef.current?.focus();
  }, [setSearchTerm]);

  return (
    <div className="flex items-center gap-2 mb-4 w-full">
      {/* Search input — grows/shrinks only with the panel, never with the spinner */}
      <div className="relative flex-1 min-w-0">
        <form autoComplete="off" className="relative" onSubmit={(e) => e.preventDefault()}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            name="session-search"
            id="session-search"
            placeholder="Search by name, date..."
            value={inputValue}
            onChange={handleChange}
            autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck="false"
            className="w-full pl-10 pr-8 py-2.5 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
          {inputValue && (
            <button type="button" onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search">
              <X className="w-4 h-4" />
            </button>
          )}
        </form>
      </div>

      {/*
        Reserved slot for the audio-sync spinner.
        It ALWAYS occupies the same width (matching the filter button),
        so the search input never resizes when audio sync starts/stops.
      */}
      <div className="relative flex-shrink-0 w-[38px] h-[38px] flex items-center justify-center">
        {isSyncingAudio && (
          <>
            {/* The spinner button (matches filter button size) */}
            <div
              role="status"
              aria-label="Syncing audio"
              className="flex h-full w-full items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-600"
            >
              <Loader2 size={18} className="animate-spin" />
            </div>

            {/* The text indicator below the spinner */}
            <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-blue-50 text-blue-600 text-xs font-medium px-2.5 py-1 rounded-md shadow-sm border border-blue-100 whitespace-nowrap z-50">
              Syncing audio…
            </div>
          </>
        )}
      </div>

      {/* Filter button — anchored to the right, fixed size */}
      <div className="relative flex-shrink-0">
      <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-expanded={filterPopoverOpen}
            aria-label="Toggle filters"
            className={`flex-shrink-0 w-[38px] h-[38px] flex items-center justify-center rounded-xl border transition-colors ${
              showActiveIndicator
                ? "bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100"
                : "hover:bg-white text-gray-700 hover:border-gray-200"
            }`}
          >
            <ListFilter
              size={20}
              className={`transition-transform duration-200 ease-in-out ${
                filterPopoverOpen ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom" align="start" sideOffset={24} alignOffset={12}
          collisionPadding={{ right: 20, bottom: 20 }}
          className="w-80 max-w-[calc(100vw-2rem)] sm:max-w-sm p-0 shadow-lg rounded-xl border border-gray-200 bg-white overflow-visible"
        >
          <div className="flex items-center justify-between px-5 pt-4 pb-1">
            <span className="text-sm font-semibold text-gray-800">Filters</span>
            <button
              type="button"
              aria-label="Close filters"
              onClick={() => setFilterPopoverOpen(false)}
              className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="overflow-visible">{filterContent}</div>
        </PopoverContent>
      </Popover>

      {/* Clear badge on the top-right corner — only while filters are active and the card is closed */}
      {showActiveIndicator && onClearFilters && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClearFilters(); }}
          aria-label="Clear all filters"
          title="Clear all filters"
          className="absolute -top-1.5 -right-1.5 z-10 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-blue-600 text-white shadow-sm ring-2 ring-white hover:bg-blue-700 transition-colors"
        >
          <X size={11} strokeWidth={3} />
        </button>
      )}
      </div>

      {extraButton && <div className="flex-shrink-0">{extraButton}</div>}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────
// AvatarMenu (unchanged — already stable)
// ─────────────────────────────────────────────────────────────────
const AvatarMenu = memo(function AvatarMenu({
  avatarUrl, initials, isMobile, showProfilePopup, setShowProfilePopup, menuItems, sizeClass,
}) {
  // Signed avatar URLs expire: on load failure refetch /auth/me once, then fall back to initials.
  const { src: avatarSrc, refreshing, onError: onAvatarError } =
    useSignedUrlRetry(avatarUrl, fetchAuthMeAvatarUrl);
  const showInitials = !avatarSrc || refreshing;

  return (
    <Popover open={showProfilePopup} onOpenChange={setShowProfilePopup}>
      <PopoverTrigger asChild>
        <button type="button"
          className={`avatar-button ${sizeClass} rounded-full bg-cyan-400 flex items-center justify-center text-white font-bold cursor-pointer hover:opacity-80 transition overflow-hidden outline-none focus:outline-none focus:ring-0 border-2 border-white shadow-md flex-shrink-0`}
          style={{ minWidth: sizeClass === 'w-14 h-14' ? '3.5rem' : '2.5rem' }}>
          {!showInitials ? (
            <img key={avatarSrc} src={avatarSrc} alt="avatar" loading="lazy"
              className="w-full h-full object-cover"
              onError={onAvatarError} />
          ) : (initials)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={isMobile ? "top" : "right"} align={isMobile ? "end" : "start"}
        sideOffset={8} alignOffset={0}
        collisionPadding={{ top: 20, bottom: 20, left: 20, right: 20 }}
        avoidCollisions={true}
        className="w-[220px] p-1 shadow-xl rounded-xl border border-gray-200/80 bg-white profile-popup z-[100]">
        <PopoverArrow className="fill-white stroke-gray-200/80" />
        <div className="py-1">
          {menuItems.map((item, idx) => (
            <button key={idx}
              className={`w-full px-4 py-2.5 text-sm rounded-lg flex items-center transition-colors ${item.className || "text-gray-700 hover:bg-gray-100"}`}
              onClick={() => { if (typeof item.action === "function") item.action(); }}>
              {item.icon}
              <span className="ml-2">{item.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
});

// ─────────────────────────────────────────────────────────────────
// SessionCircularProgress (unchanged)
// ─────────────────────────────────────────────────────────────────
const SessionCircularProgress = memo(function SessionCircularProgress({ sessionId }) {
  const progress = useSessionTranscriptionProgress(sessionId);
  if (!progress?.active) return null;
  const value = Math.min(100, Math.max(0, Number(progress.percent) || 0));
  if (value >= 100) return null;
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - value / 100);
  return (
    <div className="absolute top-2 right-2 z-20 pointer-events-none" title={`Transcription ${value}%`}>
      <div className="relative h-9 w-9 rounded-full bg-white/90">
        <svg className="h-9 w-9 -rotate-90" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="3" />
          <circle cx="18" cy="18" r={radius} fill="none" stroke="#2563eb" strokeWidth="3"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
            className="transition-all duration-300" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-blue-700">
          {value}
        </div>
      </div>
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════
// SessionManager
// ═════════════════════════════════════════════════════════════════
function SessionManager({
  activeSession,
  isRecordingActive = false,
  parkedSessionIds = [],
  isParkingCapture = false,
  isDemoActive = false,
  demoSessionEntry = null,
  onSelectDemoVisit,
  onDeleteDemoVisit,
  onCreateSession,
  onSelectSession,
  onDeleteSession,
  onUpdateSessionName,
  isCreatingSession,
  mobileMenuOpen,
  mobileSection,
  setMobileMenuOpen,
  setMobileSection,
  sessionsVersion,
  newVisitButtonRef,
  keepVisitsPanelOpen = false,
  sessionListPatch = null,
  onRestartTutorial = () => {},
}) {
  const [sessionVisible, setSessionVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname === "/scribe";
  });
  const [isMinimized, setIsMinimized] = useState(false);
  const [, setIsHovered] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null);
  const [creatorFilter, setCreatorFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [sessionCreators, setSessionCreators] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [userSessions, setUserSessions] = useState([]);
  const [liveParkedSessionIds, setLiveParkedSessionIds] = useState(() => listParkedCaptureSessionIds());
  const [visitTypeFilter, setVisitTypeFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [visitTypeOptions, setVisitTypeOptions] = useState([]);
  const [loadingVisitTypes, setLoadingVisitTypes] = useState(false);
  const [visitTypeOpen, setVisitTypeOpen] = useState(false);
  const [genderOpen, setGenderOpen] = useState(false);

  useEffect(() => subscribeParkedCaptures(setLiveParkedSessionIds), []);
  useEffect(() => {
    setLiveParkedSessionIds(listParkedCaptureSessionIds());
  }, [parkedSessionIds, sessionsVersion, activeSession]);

  const parkedIdSet = useMemo(() => {
    return new Set(
      [...(parkedSessionIds || []), ...(liveParkedSessionIds || [])].map(String)
    );
  }, [parkedSessionIds, liveParkedSessionIds]);

  const SESSIONS_PAGE_SIZE = 30;
  const [sessionsOffset, setSessionsOffset] = useState(0);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [assignedSessions, setAssignedSessions] = useState([]);
  // NOTE: `loading` is now used ONLY for the sessions list (initial load +
  // pagination). It is deliberately NOT reused for delete / bulk-delete any
  // more — see `isDeletingSession` / `isBulkDeleting` below. Previously every
  // delete / bulk-delete call flipped `loading` to true, which caused the
  // whole visits panel to flash skeletons and disabled the New Visit button.
  const [loading, setLoading] = useState(true);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const helpButtonRef = useRef(null);
  const avatarButtonRef = useRef(null);
  const [helpDropdownPosition, setHelpDropdownPosition] = useState({ top: 0, left: 0 });
  const [editingSession, setEditingSession] = useState(null);
  const [editingName, setEditingName] = useState("");
  const {
    trialStatus, isChartCreationLocked, isLimitExempt,
    getAccountLimitMessage, refreshTrialStatus,
  } = useTrial();
  const { currentUser: userContextProfile, logout: performLogout } = useAuth();
  const [loadingAssignableUsers, setLoadingAssignableUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showCalendar, setShowCalendar] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const lastSessionsBootstrapKeyRef = useRef(null);
  const sessionsScrollRestoreRef = useRef(0);
  const lastUserScrollAtRef = useRef(0);
  const isLoadingMoreSessionsRef = useRef(false);
  const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);
  const sessionsOffsetRef = useRef(0);
  const inFlightSessionSelectRef = useRef(null);
  const lastSessionSelectRef = useRef({ id: null, ts: 0 });
  const loadedOffsetsRef = useRef(new Set([0]));
  const loadedSessionIdsRef = useRef(new Set());
  const creatorFilterRef = useRef(creatorFilter);
  const desktopSessionsListRef = useRef(null);
  const mobileSessionsListRef = useRef(null);
  const suppressScrollRestoreRef = useRef(false);
  const onDesktopSessionsListRef = useCallback((el) => { desktopSessionsListRef.current = el; }, []);
  const onMobileSessionsListRef = useCallback((el) => { mobileSessionsListRef.current = el; }, []);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const rawUrlSessionId = searchParams.get("sessionId");
  // Prefer the in-memory demo session. URL sessionId can still point at the
  // previous real visit for a tick after the user clicks Demo Visit, and
  // treating the URL as source of truth made the demo look unselected / blank.
  const resolvedActiveSession =
    activeSession === "demo-session" || isDemoActive
      ? (activeSession ?? null)
      : rawUrlSessionId && rawUrlSessionId !== "undefined" && rawUrlSessionId !== "null"
        ? rawUrlSessionId
        : (activeSession ?? null);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("user");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isScriber, setIsScriber] = useState(false);
  const [isFullTimeScribe, setIsFullTimeScribe] = useState(false);
  const [isPartTimeScribe, setIsPartTimeScribe] = useState(false);
  const { unreadCount } = useNotifications();
  const isAnalyticsRoute = location.pathname === "/admin/analytics";
  const isVisitsRoute = location.pathname === "/scribe";
  const isAdminAppRoute = location.pathname.startsWith("/admin");
  const isDashboardRoute = location.pathname === "/scribe/dashboard";
  const isTemplatesRoute = location.pathname === "/scribe/templates";
  const isSettingsRoute = location.pathname === "/scribe/settings";
  const isAssignRoute = location.pathname === "/scribe/assign-task";
  const {
    audioCache, isLoading: isMappingAudio, triggerAudioFetch,
    getPlayableAudioUrl, getCachedBlobUrl, awaitCachedBlobUrl,
    keepAudioElementUrlFresh, ensureAudioElementUrlFresh,
  } = useAudioCache();

  const [playingAudio, setPlayingAudio] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(new Set());
  const [audioElements, setAudioElements] = useState(new Map());
  const [audioErrors, setAudioErrors] = useState(new Map());

  useEffect(() => {
    const loadVisitTypeOptions = async () => {
      setLoadingVisitTypes(true);
      try {
        const response = await visitTypeApiService.getVisitTypeOptions();
        if (response.success) setVisitTypeOptions(response.data.visitTypes || []);
      } catch (error) { console.error('Failed to load visit type options:', error); }
      finally { setLoadingVisitTypes(false); }
    };
    loadVisitTypeOptions();
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // Audio prefetch
  // ─────────────────────────────────────────────────────────────────
  // Previously this ran with `force: sessionsVersion > 0`, which meant
  // EVERY sessionsVersion bump (New visit, Update, Delete, Rename, Bulk
  // delete, …) triggered a full re-sync of the user's audio → the
  // "Syncing audio…" spinner in SearchFilterRow lit up on every click.
  //
  // Fix: (a) never force — `force:false` keeps anything already cached, so
  // only genuinely new sessions are fetched; (b) debounce 800ms so rapid
  // consecutive mutations (e.g. renaming several sessions) collapse into a
  // single prefetch pass.
  useEffect(() => {
    if (!userContextProfile) return;
    const uid = userContextProfile?.userId ?? userContextProfile?.userid ?? userContextProfile?.id;
    if (!uid) return;
    const timer = setTimeout(() => {
      prefetchUserAudioFiles({ force: false, userId: String(uid) }).catch(() => {});
    }, 800);
    return () => clearTimeout(timer);
  }, [userContextProfile, sessionsVersion]);

  const getSessionsCacheKey = useCallback(() => {
    const uid = userContextProfile?.userId ?? userContextProfile?.userid ?? userContextProfile?.id;
    return buildSessionsCacheKey(uid);
  }, [userContextProfile]);

  const writeSessionsCache = useCallback((next = {}) => {
    const key = getSessionsCacheKey();
    if (!key) return;
    const loadedIds = loadedSessionIdsRef.current;
    const cachedLoadedSessions = sortSessionsNewestFirst(
      filterVisibleSessions(
        userSessions.filter((s) => {
          if (isDemoSession(s)) return false;
          const sid = s?.id ?? s?.sessionId;
          return sid != null && loadedIds.has(String(sid));
        }),
      ),
    );
    const payload = {
      sessions: cachedLoadedSessions,
      sessionsOffset, hasMoreSessions,
      scrollTop: sessionsScrollRestoreRef.current || 0,
      loadedOffsets: Array.from(loadedOffsetsRef.current),
      loadedSessionIds: Array.from(loadedSessionIdsRef.current),
      ...next,
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
  }, [getSessionsCacheKey, userSessions, sessionsOffset, hasMoreSessions]);

  const purgeDeletedSessionFromCache = useCallback((sessionId) => {
    const uid = userContextProfile?.userId ?? userContextProfile?.userid ?? userContextProfile?.id;
    removeSessionFromListCache(uid, sessionId);
    loadedSessionIdsRef.current.delete(String(sessionId));
  }, [userContextProfile]);

  const readSessionsCache = useCallback(() => {
    const uid = userContextProfile?.userId ?? userContextProfile?.userid ?? userContextProfile?.id;
    return readSessionsListCache(uid);
  }, [userContextProfile]);

  useEffect(() => { sessionsOffsetRef.current = sessionsOffset; }, [sessionsOffset]);


  const TRANSCRIPTION_VISIBLE_STAGES = useMemo(
    () => new Set(["queued","uploading","processing","transcribing","finalizing","generating_soap","completed"]),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const hydrateProgress = async () => {
      try {
        const response = await aiService.getTranscriptionJobs({ limit: 100 });
        const jobs = response?.data?.body?.data?.jobs || response?.data?.data?.jobs || [];
        if (cancelled || !Array.isArray(jobs)) return;
        const newestBySession = new Map();
        for (const job of jobs) {
          const sidRaw = job?.sessionId;
          if (sidRaw == null || sidRaw === "") continue;
          const stage = String(job?.stage || "").toLowerCase();
          const status = String(job?.status || "").toLowerCase();
          const isProcessingLike = status === "queued" || status === "processing";
          if (!TRANSCRIPTION_VISIBLE_STAGES.has(stage) && !isProcessingLike) continue;
          const sid = String(sidRaw);
          const rawPercent = Number(job?.percent ?? job?.progress ?? 0);
          const effectivePercent =
            stage === "completed" && (!Number.isFinite(rawPercent) || rawPercent <= 0) ? 100 : rawPercent;
          const percent = Math.min(100, Math.max(0, Math.round(effectivePercent)));
          const updatedAt = new Date(job?.updatedAt || 0).getTime();
          const existing = newestBySession.get(sid);
          if (!existing || updatedAt > existing.updatedAt) {
            newestBySession.set(sid, {
              percent, stage, status,
              jobId: job?.jobId ?? job?.id ?? null, updatedAt,
            });
          }
        }
        for (const [sid, row] of newestBySession.entries()) {
          const stage = String(row.stage || "").toLowerCase();
          if (stage === "completed" || stage === "failed") continue;
          applyLocalSessionProgress(sid, {
            jobId: row.jobId, stage: row.stage,
            status: row.status || "processing",
            percent: row.percent, active: true,
          });
        }
      } catch { /* best effort */ }
    };
    hydrateProgress();
    const timer = setInterval(hydrateProgress, 7000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [TRANSCRIPTION_VISIBLE_STAGES]);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const hoverTimerRef = useRef(null);
  const [isHoverLocked, setIsHoverLocked] = useState(false);

  const handleMouseEnterVisits = () => {
    if (isMobile || isHoverLocked) return;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setSessionVisible(true);
  };
  const handleMouseLeaveVisits = () => {
    if (isMobile || isHoverLocked) return;
    hoverTimerRef.current = setTimeout(() => setSessionVisible(false), 200);
  };
  const handlePanelMouseEnter = () => {
    if (isMobile) return;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  };
  const handlePanelMouseLeave = () => {
    if (isMobile || isHoverLocked) return;
    hoverTimerRef.current = setTimeout(() => setSessionVisible(false), 200);
  };
  const handleVisitsClick = () => {
    if (isMobile) return;
    if (isHoverLocked) { setSessionVisible(false); setIsHoverLocked(false); }
    else { setSessionVisible(true); setIsHoverLocked(true); }
  };
  const handleClosePanel = () => { setSessionVisible(false); setIsHoverLocked(false); };

  useEffect(() => {
    if (!keepVisitsPanelOpen) return;
    if (isMobile) setMobileMenuOpen(true);
    else { setSessionVisible(true); setIsHoverLocked(true); }
  }, [keepVisitsPanelOpen, isMobile, setMobileMenuOpen]);

  useEffect(() => {
    if (!isDemoActive) return;
    if (isMobile) setMobileMenuOpen(true);
    else { setSessionVisible(true); setIsHoverLocked(true); }
  }, [isDemoActive, isMobile]);

  const assignNewVisitButtonRef = (element) => {
    if (newVisitButtonRef) newVisitButtonRef.current = element;
  };

  const normalizeSession = useCallback((raw = {}) => {
    const base = sessionService.formatSessionForDisplay ? sessionService.formatSessionForDisplay(raw) : {};
    const idRaw = base.id || raw.id || raw.sessionId;
    const id = idRaw != null && idRaw !== "" ? String(idRaw) : idRaw;
    const sessionName = base.sessionName || base.name || raw.sessionName || raw.name || "Untitled Session";
    const sid = raw.sessionId != null && raw.sessionId !== "" ? String(raw.sessionId) : id;
    return {
      ...raw, ...base, id, sessionId: sid,
      name: sessionName, sessionName,
      createdAt: base.createdAt || raw.createdAt || raw.visitDate || null,
      visitDate: base.visitDate || raw.visitDate || null,
    };
  }, []);

  const mergeUniqueSessions = useCallback((prev, next) => {
    const map = new Map();
    [...prev, ...next].forEach((item) => {
      const rawId = item?.id ?? item?.sessionId;
      if (rawId == null || rawId === "") return;
      const key = String(rawId);
      map.set(key, {
        ...item, id: key,
        sessionId: item.sessionId != null && item.sessionId !== "" ? String(item.sessionId) : key,
      });
    });
    return sortSessionsNewestFirst(Array.from(map.values()));
  }, []);

  const appendUniqueSessions = useCallback((prev, next) => {
    const existingIds = new Set(
      prev.map((item) => item?.id ?? item?.sessionId)
        .filter((id) => id != null && id !== "").map((id) => String(id)),
    );
    const additions = [];
    next.forEach((item) => {
      const rawId = item?.id ?? item?.sessionId;
      if (rawId == null || rawId === "") return;
      const key = String(rawId);
      if (existingIds.has(key)) return;
      existingIds.add(key);
      additions.push({
        ...item, id: key,
        sessionId: item.sessionId != null && item.sessionId !== "" ? String(item.sessionId) : key,
      });
    });
    return [...prev, ...additions];
  }, []);

  const upsertSessionPreserveOrder = useCallback((prev, session) => {
    const rawId = session?.id ?? session?.sessionId;
    if (rawId == null || rawId === "") return prev;
    const key = String(rawId);
    const normalized = {
      ...session, id: key,
      sessionId: session?.sessionId != null && session.sessionId !== "" ? String(session.sessionId) : key,
      createdAt: session?.createdAt || session?.visitDate || new Date().toISOString(),
      visitDate: session?.visitDate || session?.createdAt || null,
    };
    const idx = prev.findIndex((item) => String(item.id) === key || String(item.sessionId) === key);
    if (idx === -1) return sortSessionsNewestFirst([normalized, ...prev]);
    const next = [...prev];
    next[idx] = { ...next[idx], ...normalized };
    return next;
  }, []);

  const sessionListContainsId = useCallback((list, sessionId) => {
    if (sessionId == null || sessionId === "") return true;
    const sid = String(sessionId);
    return list.some((s) => String(s.id) === sid || String(s.sessionId) === sid);
  }, []);

  const isActiveSessionId = useCallback(
    (sessionId) => resolvedActiveSession != null && String(resolvedActiveSession) === String(sessionId),
    [resolvedActiveSession],
  );

  const extractSessionFromGetResponse = (response) => {
    if (!response) return null;
    const ok = response.success === true || response.statusCode === 200 || response.statusCode === true;
    const fromData =
      response.data?.session || response.data?.data?.session ||
      response.body?.session || response.body?.data?.session || response.body?.data?.data?.session;
    if (fromData) return fromData;
    if (response.sessionId != null && (response.sessionName != null || response.visitDate != null)) return response;
    if (ok && response.data?.sessionId != null) return response.data;
    if (ok && response.body?.sessionId != null) return response.body;
    return null;
  };

  useEffect(() => {
    if (!userContextProfile || !resolvedActiveSession) return;
    if (isDemoActive || resolvedActiveSession === "demo-session") return;
    const canHydrateOwnSessionList =
      isAdmin || userRole === "user" || userRole === "1" || userRole === 1 ||
      isFullTimeScribe || isPartTimeScribe;
    if (!canHydrateOwnSessionList) return;
    const targetId = String(resolvedActiveSession);
    const alreadyInList = sessionListContainsId(userSessions, targetId);
    if (alreadyInList) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await sessionService.getSession(targetId);
        const sessionData = extractSessionFromGetResponse(response);
        if (!sessionData) return;
        const row = normalizeSession(sessionData);
        if (cancelled) return;
        setUserSessions((prev) => upsertSessionPreserveOrder(prev, row));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [
    resolvedActiveSession, userSessions, userContextProfile, isAdmin, userRole,
    isFullTimeScribe, isPartTimeScribe, normalizeSession, upsertSessionPreserveOrder,
    sessionListContainsId, isDemoActive,
  ]);

  const lastAppliedPatchNonceRef = useRef(null);
  useEffect(() => {
    if (!sessionListPatch || sessionListPatch.nonce == null) return;
    if (lastAppliedPatchNonceRef.current === sessionListPatch.nonce) return;
    lastAppliedPatchNonceRef.current = sessionListPatch.nonce;
    const { op } = sessionListPatch;
    if (op === "add" && sessionListPatch.session) {
      const row = normalizeSession(sessionListPatch.session);
      setUserSessions((prev) => upsertSessionPreserveOrder(prev, row));
      loadedSessionIdsRef.current.add(String(row.id));
      return;
    }
    if (op === "upsert" && sessionListPatch.sessionId != null) {
      const sid = String(sessionListPatch.sessionId);
      setUserSessions((prev) => {
        const idx = prev.findIndex((s) => String(s.id) === sid || String(s.sessionId) === sid);
        if (idx === -1) {
          return upsertSessionPreserveOrder(prev, {
            id: sid, sessionId: sid, ...(sessionListPatch.patch || {}),
          });
        }
        const next = [...prev];
        next[idx] = { ...next[idx], ...(sessionListPatch.patch || {}) };
        return next;
      });
      return;
    }
    if (op === "remove" && sessionListPatch.sessionId != null) {
      const sid = String(sessionListPatch.sessionId);
      setUserSessions((prev) =>
        prev.filter((s) => String(s.id) !== sid && String(s.sessionId) !== sid),
      );
      loadedSessionIdsRef.current.delete(sid);
      return;
    }
    if (op === "removeMany" && Array.isArray(sessionListPatch.sessionIds)) {
      const ids = new Set(sessionListPatch.sessionIds.map(String));
      setUserSessions((prev) =>
        prev.filter((s) => !ids.has(String(s.id)) && !ids.has(String(s.sessionId))),
      );
      ids.forEach((id) => loadedSessionIdsRef.current.delete(id));
      return;
    }
  }, [sessionListPatch, normalizeSession, upsertSessionPreserveOrder]);

  const fetchAssignableUsers = async () => {
    try {
      setLoadingAssignableUsers(true);
      const response = await adminService.getAssignableUsers();
      if (response.success) setAssignableUsers(response.data?.users || []);
    } catch (error) { console.error("Error fetching assignable users:", error); }
    finally { setLoadingAssignableUsers(false); }
  };
  const fetchSessionCreators = async () => {
    try {
      const [creatorsRes, usersRes] = await Promise.all([
        adminService.getSessionCreators(),
        adminService.getFilterUsers(),
      ]);
      const byId = new Map();
      const addUsers = (list = []) => {
        list.forEach((user) => {
          if (!user?.userId) return;
          const name = getDisplayCreatorName(user?.name, user?.email);
          if (!name) return;
          byId.set(user.userId, {
            userId: user.userId,
            email: user.email || "",
            name,
          });
        });
      };
      addUsers(usersRes.success ? usersRes.data?.users : []);
      addUsers(creatorsRes.success ? creatorsRes.data?.users : []);
      setSessionCreators(Array.from(byId.values()));
    } catch (error) { console.error("Error fetching session creators:", error); }
  };
  useEffect(() => {
    if (isAdmin) {
      fetchAssignableUsers();
      fetchSessionCreators();
    }
  }, [isAdmin]);

  const shouldShowFeature = (featureType) => {
    const isScriberUser = isScriber && !isAdmin;
    const authRole = String(userContextProfile?.role || "").toLowerCase();
    const authRoleId = Number(userContextProfile?.profile?.roleId || userContextProfile?.roleId);
    const isAccountAdmin = isAdmin || userRole === "admin" || authRole.includes("admin");
    const isAccountDeveloper =
      userRole === "developer" || authRole.includes("developer") || authRoleId === 5;
    const isAccountScribe =
      isScriber || userRole === "scribe_parttime" || userRole === "scribe_fulltime" || authRole.includes("scribe");
    const canCreateSessions =
      isAccountAdmin || isAccountDeveloper || userRole === "user" || userRole === "1" || userRole === 1 ||
      isFullTimeScribe || isPartTimeScribe;
    const canViewDashboard = isScriberUser;
    const canViewAssignedSessions = isScriberUser;
    const canViewOwnSessions =
      isAdmin || userRole === "user" || userRole === "1" || userRole === 1 ||
      isFullTimeScribe || isPartTimeScribe;
    switch (featureType) {
      case "session-management": return canViewOwnSessions;
      case "assigned-sessions": return canViewAssignedSessions;
      case "admin-only": return isAdmin;
      case "subscription": return userRole === "user" || userRole === "1" || userRole === 1;
      case "upgrade": return userRole === "user" || userRole === "1" || userRole === 1;
      case "create-session":
        return (
          canCreateSessions &&
          (isAccountAdmin || isAccountDeveloper || isAccountScribe || isLimitExempt || !isChartCreationLocked)
        );
      case "dashboard": return canViewDashboard;
      case "analytics": return isAdmin;
      case "tasks": return isAdmin;
      case "audio-controls": return true;
      case "edit-session": return true;
      case "delete-session": return true;
      case "assign-task": return false;
      default: return true;
    }
  };

  const audioElementsRef = useRef(audioElements);
  audioElementsRef.current = audioElements;

  useEffect(() => {
    return () => {
      audioElementsRef.current.forEach((audio) => {
        if (!audio) return;
        audio._vbotCleared = true;
        audio._vbotStopUrlRefresh?.();
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      });
    };
  }, []);

  const audioStatusMap = useMemo(() => {
    const statusMap = new Map();
    audioCache.forEach((audioFile, sessionId) => {
      const sid = String(sessionId);
      statusMap.set(sid, {
        hasAudio: true,
        isExpired: !!audioFile.isExpired,
        isAccessible: !!audioFile.isAccessible && !audioFile.isExpired,
      });
    });
    return statusMap;
  }, [audioCache]);

  const getAudioStatus = useCallback(
    (sessionId) =>
      audioStatusMap.get(String(sessionId)) || {
        hasAudio: false, isExpired: false, isAccessible: false,
      },
    [audioStatusMap],
  );

  const handleAudioPlay = useCallback(
    async (sessionId, e) => {
      e.stopPropagation();
      if (isPartTimeScribe) {
        const sidPt = String(sessionId);
        const assignedSession = assignedSessions.find((s) => String(s.session?.sessionId) === sidPt);
        if (!assignedSession) {
          toast.error("You don't have permission to play audio for this session");
          return;
        }
      } else if (isFullTimeScribe) {
        const sid = String(sessionId);
        const isOwnSession = userSessions.find((s) => String(s.id) === sid || String(s.sessionId) === sid);
        const isAssignedSession = assignedSessions.find((s) => String(s.session?.sessionId) === sid);
        if (!isOwnSession && !isAssignedSession) {
          toast.error("You don't have permission to play audio for this session");
          return;
        }
      } else if (isScriber && !isAdmin && !isFullTimeScribe && !isPartTimeScribe) {
        toast.error("You don't have permission to play audio for this session");
        return;
      }

      try {
        if (playingAudio && playingAudio !== sessionId) {
          const currentAudio = audioElements.get(playingAudio);
          if (currentAudio) currentAudio.pause();
          setLoadingAudio((prev) => {
            const newSet = new Set(prev);
            newSet.delete(playingAudio);
            return newSet;
          });
        }
        if (playingAudio === sessionId) {
          const audio = audioElements.get(sessionId);
          if (audio) { audio.pause(); setPlayingAudio(null); }
          return;
        }
        const existingAudio = audioElements.get(sessionId);
        const existingSrc = (existingAudio?.src || "").trim();
        const hasRealSrc =
          !!existingSrc && existingSrc !== window.location.href && !existingAudio._vbotCleared;
        const readyBlob = getCachedBlobUrl(sessionId);
        if (
          existingAudio && hasRealSrc && readyBlob &&
          !existingSrc.startsWith("blob:") && existingAudio.paused
        ) {
          const t = existingAudio.currentTime || 0;
          const rate = existingAudio.playbackRate || 1;
          const vol = existingAudio.volume;
          existingAudio._vbotHotSwap = true;
          await new Promise((resolve) => {
            const done = () => { existingAudio._vbotHotSwap = false; resolve(); };
            existingAudio.addEventListener("loadedmetadata", () => {
              try {
                if (Number.isFinite(t) && t > 0) existingAudio.currentTime = t;
                existingAudio.playbackRate = rate;
                existingAudio.volume = vol;
              } catch { /* ignore */ }
              done();
            }, { once: true });
            existingAudio.addEventListener("error", done, { once: true });
            existingAudio.src = readyBlob;
            existingAudio.preload = "auto";
          });
        }
        const canReuse = !!existingAudio && hasRealSrc && existingAudio.error == null;
        if (canReuse) {
          try {
            // The signed URL may have expired (or be about to) while paused; swap in a fresh one first.
            await ensureAudioElementUrlFresh(existingAudio, sessionId);
            if (existingAudio.ended) existingAudio.currentTime = 0;
            await existingAudio.play();
            setPlayingAudio(sessionId);
            setAudioErrors((prev) => {
              if (!prev.has(sessionId)) return prev;
              const newMap = new Map(prev);
              newMap.delete(sessionId);
              return newMap;
            });
            return;
          } catch { /* fall through */ }
        }
        setLoadingAudio((prev) => new Set(prev).add(sessionId));
        setAudioErrors((prev) => {
          const newMap = new Map(prev);
          newMap.delete(sessionId);
          return newMap;
        });
        let ownerUserId = null;
        if (isAdmin) {
          const cachedFile = audioCache.get(sessionId);
          if (cachedFile && cachedFile.userId) ownerUserId = cachedFile.userId;
        }
        const audioUrl = await getPlayableAudioUrl(sessionId, { ownerUserId });
        if (!audioUrl) throw new Error("No playable audio URL");
        if (existingAudio) {
          existingAudio._vbotCleared = true;
          existingAudio._vbotStopUrlRefresh?.();
          existingAudio.pause();
          existingAudio.removeAttribute("src");
          existingAudio.load();
        }
        const audio = new Audio();
        audio.src = audioUrl;
        audio.preload = "auto";
        // Signed URLs expire. Keeps playback uninterrupted by refreshing the URL in the background and
        // only swapping it in while idle. Must be attached BEFORE the listeners below so its error /
        // seek handling runs first. No-op for blob: sources.
        if (typeof audioUrl === "string" && !audioUrl.startsWith("blob:")) {
          audio._vbotStopUrlRefresh = keepAudioElementUrlFresh(audio, sessionId, { ownerUserId });
        }
        const clearLoading = () => {
          setLoadingAudio((prev) => {
            const newSet = new Set(prev);
            newSet.delete(sessionId);
            return newSet;
          });
        };
        audio.addEventListener("canplay", clearLoading);
        audio.addEventListener("canplaythrough", clearLoading);
        audio.addEventListener("ended", () => { setPlayingAudio(null); clearLoading(); });
        audio.addEventListener("error", () => {
          if (audio._vbotCleared || audio._vbotHotSwap) return;
          setPlayingAudio(null);
          clearLoading();
          setAudioErrors((prev) => new Map(prev).set(sessionId, "Failed to load audio file"));
        });
        audio.volume = 1.0;
        setAudioElements((prev) => new Map(prev).set(sessionId, audio));
        try {
          await audio.play();
          setPlayingAudio(sessionId);
          clearLoading();
        } catch { setPlayingAudio(null); }
        if (typeof audioUrl === "string" && !audioUrl.startsWith("blob:")) {
          awaitCachedBlobUrl(sessionId).catch(() => {});
        }
      } catch (error) {
        console.error("Audio play error:", error);
        setLoadingAudio((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
      }
    },
    [
      playingAudio, audioElements, getPlayableAudioUrl, getCachedBlobUrl, awaitCachedBlobUrl,
      keepAudioElementUrlFresh, ensureAudioElementUrlFresh,
      isAdmin, isScriber, isFullTimeScribe, isPartTimeScribe, assignedSessions, userSessions, audioCache,
    ],
  );

  const stopAllAudio = () => {
    if (playingAudio) {
      const currentAudio = audioElements.get(playingAudio);
      if (currentAudio) currentAudio.pause();
      setPlayingAudio(null);
      setLoadingAudio(new Set());
      setAudioErrors(new Map());
    }
  };

  const [, setActiveSessionName] = useState("");

  useEffect(() => {
    if (isAdminAppRoute || isAnalyticsRoute) {
      setSessionVisible(false); setIsHoverLocked(false);
    } else if (location.pathname.startsWith("/scribe/")) {
      setSessionVisible(false); setIsHoverLocked(false);
    } else if (!location.pathname.startsWith("/scribe")) {
      setSessionVisible(false);
    }
    const isScribeSubRoute = location.pathname.startsWith("/scribe/");
    if (
      !isAdminAppRoute && location.pathname !== "/scribe" &&
      !isScribeSubRoute && searchParams.get("sessionId")
    ) {
      const sessionId = searchParams.get("sessionId");
      navigate(`/scribe?sessionId=${sessionId}`);
    }
  }, [location.pathname, searchParams, navigate, isAdminAppRoute, isAnalyticsRoute]);

  const showVisitsPanel = isVisitsRoute && sessionVisible;

  const getSessionName = (sessionId, sessions) => {
    const sid = String(sessionId);
    const session = sessions.find((s) => String(s.id) === sid || String(s.sessionId) === sid);
    if (!session) return "No active session";
    const sessionDate = new Date(session.createdAt || session.date);
    const dateStr = sessionDate.toISOString().split("T")[0];
    const sameDateSessions = sessions
      .filter((s) => {
        const sDate = new Date(s.createdAt || s.date);
        return sDate.toISOString().split("T")[0] === dateStr;
      })
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const sessionIndex = sameDateSessions.findIndex(
      (s) => String(s.id) === sid || String(s.sessionId) === sid,
    );
    return `Visit ${dateStr} #${sessionIndex + 1}`;
  };

  useEffect(() => {
    if (resolvedActiveSession && userSessions.length > 0) {
      setActiveSessionName(getSessionName(resolvedActiveSession, userSessions));
    }
  }, [resolvedActiveSession, userSessions]);

  const getSessionCreator = (session) => {
    const soapUserId = session.soapNote?.userId;
    const soapUser = soapUserId
      ? sessionCreators.find((user) => user.userId === soapUserId)
      : null;
    return getDisplayCreatorName(
      soapUser?.name,
      soapUser?.email,
      session.creator?.name,
      session.creator?.email,
    );
  };
  const getSessionAssignee = (session) => {
    const soapNote = session.soapNote;
    if (!soapNote || !soapNote.assignedTo) return null;
    if (soapNote.assignedToUser) {
      const name = `${soapNote.assignedToUser.firstName || ""} ${soapNote.assignedToUser.lastName || ""}`.trim();
      if (name) return name;
    }
    const user = assignableUsers.find((u) => u.userId === soapNote.assignedTo);
    if (user?.name) return user.name;
    if (user?.email) return user.email;
    return "Unknown User";
  };

  const uniqueCreators = useMemo(() => {
    const byKey = new Map();
    const addCreator = (name, userId, email) => {
      const label = getDisplayCreatorName(name, email);
      if (!label) return;
      const key = userId || label.toLowerCase();
      if (!byKey.has(key)) {
        byKey.set(key, { value: userId || label, label });
      }
    };
    sessionCreators.forEach((user) => {
      addCreator(user?.name, user?.userId, user?.email);
    });
    userSessions.forEach((session) => {
      addCreator(
        getSessionCreator(session),
        session.soapNote?.userId || session.creator?.userId,
        session.creator?.email,
      );
    });
    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [userSessions, sessionCreators]);

  const uniqueAssignees = useMemo(() => {
    const nameSet = new Set(assignableUsers.map((u) => u.name).filter(Boolean));
    userSessions.forEach((session) => {
      const assignee = getSessionAssignee(session);
      if (assignee && assignee !== "-") nameSet.add(assignee);
    });
    return ["Unassigned", ...Array.from(nameSet).sort()];
  }, [userSessions, assignableUsers]);

  const filterSessionsByDate = (session) => {
    if (dateFilter === "all") return true;
    const sessionDate = new Date(session.createdAt || session.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDay = new Date(sessionDate);
    sessionDay.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil(Math.abs(today - sessionDay) / (1000 * 60 * 60 * 24));
    if (dateFilter === "custom" && customDateRange.from && customDateRange.to) {
      const fromDate = new Date(customDateRange.from);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = new Date(customDateRange.to);
      toDate.setHours(23, 59, 59, 999);
      return sessionDate >= fromDate && sessionDate <= toDate;
    }
    switch (dateFilter) {
      case "today": return diffDays === 0;
      case "yesterday": return diffDays === 1;
      case "last7": return diffDays <= 7;
      case "last30": return diffDays <= 30;
      case "last90": return diffDays <= 90;
      case "last12months": return diffDays <= 365;
      default: return true;
    }
  };

  const getSessionDisplayName = (session) => {
    if (session.patient?.name) return session.patient.name;
    if (session.name && session.name !== "Untitled Session") return cleanSessionName(session.name);
    return "Unknown";
  };

  const getFormattedDateOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const getFormattedTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  };
  const getSessionStatus = (session) => {
    if (session.hasSoapNote) return { label: "Completed", variant: "default" };
    return { label: "Not started", variant: "secondary" };
  };

  const allSessions = useMemo(
    () => mergeDemoSessionIntoList(userSessions, demoSessionEntry, { isAdmin }),
    [userSessions, demoSessionEntry, isAdmin],
  );

  const filteredSessions = allSessions.filter((session) => {
    if (session.status === "deleted") return false;
    if (session.hasSoapNote && !isAdmin && !session.isDemoEntry) return false;
    const sessionName = getSessionDisplayName(session);
    const matchesName = sessionName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPatient = session.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const sessionDate = new Date(session.createdAt || session.date);
    const matchesDate = sessionDate.toLocaleDateString().includes(searchTerm);
    const matchesSearch = matchesName || matchesPatient || matchesDate || searchTerm === "";
    const matchesDateFilter = filterSessionsByDate(session);
    let matchesVisitType = true;
    if (visitTypeFilter !== "all") {
      const sessionVisitType = (session.patient?.visitType || session.visitType || "")
        .toString().trim().toLowerCase();
      matchesVisitType = sessionVisitType !== "" && sessionVisitType === visitTypeFilter.toLowerCase();
    }
    let matchesGender = true;
    if (genderFilter !== "all") {
      const sessionGender = session.patient?.gender || session.gender || "";
      matchesGender = sessionGender.toLowerCase() === genderFilter.toLowerCase();
    }
    if (isAdmin) {
      const sessionCreator = getSessionCreator(session);
      const sessionCreatorId = session.creator?.userId;
      const sessionAssignee = getSessionAssignee(session);
      const matchesCreator =
        creatorFilter === "all" ||
        session.soapNote?.userId === creatorFilter ||
        sessionCreatorId === creatorFilter ||
        sessionCreator === creatorFilter;
      let matchesAssignee = true;
      if (assigneeFilter === "all") matchesAssignee = true;
      else if (assigneeFilter === "Unassigned")
        matchesAssignee = !sessionAssignee || sessionAssignee === "-" || sessionAssignee === "Unassigned";
      else matchesAssignee = sessionAssignee === assigneeFilter;
      return matchesSearch && matchesDateFilter && matchesCreator && matchesAssignee &&
        matchesVisitType && matchesGender;
    }
    return matchesSearch && matchesDateFilter && matchesVisitType && matchesGender;
  });

  const filteredAssignedSessions = assignedSessions.filter((item) => {
    const session = item.session;
    const patient = item.patient;
    const matchesPatient =
      patient?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
    const sessionName = session?.sessionName || "";
    const matchesSession = sessionName.toLowerCase().includes(searchTerm.toLowerCase());
    const sessionDate = new Date(session?.visitDate);
    const matchesDate = sessionDate.toLocaleDateString().includes(searchTerm);
    const matchesStatus = item.status?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSearch = matchesPatient || matchesSession || matchesDate || matchesStatus || searchTerm === "";
    const matchesDateFilter = session?.visitDate
      ? filterSessionsByDate({ createdAt: session.visitDate, date: session.visitDate })
      : true;
    return matchesSearch && matchesDateFilter;
  });

  const sortedAssignedSessions = [...filteredAssignedSessions].sort(
    (a, b) => new Date(b.session?.visitDate || b.createdAt) - new Date(a.session?.visitDate || a.createdAt),
  );

  const sortedSessions = useMemo(
    () => sortSessionsNewestFirst(filteredSessions),
    [filteredSessions],
  );

  // A brand-new user only has the demo visit, which has no audio to sync, so
  // the "Syncing audio…" indicator must not light up for them.
  const hasRealSessions = useMemo(
    () => userSessions.some((s) => !s.isDemoEntry && s.id !== "demo-session" && s.status !== "deleted"),
    [userSessions],
  );

  // Drop selections that are no longer in the visible list (e.g. hidden by a new
  // search/filter or removed), so bulk delete only ever touches what the user sees.
  useEffect(() => {
    if (selectedSessionIds.size === 0) return;
    const visible = new Set(sortedSessions.map((s) => s.id));
    let changed = false;
    const next = new Set();
    selectedSessionIds.forEach((id) => {
      if (visible.has(id)) next.add(id);
      else changed = true;
    });
    if (changed) setSelectedSessionIds(next);
  }, [sortedSessions, selectedSessionIds]);

  useEffect(() => {
    // The demo entry is now derived via allSessions; nothing to do here.
  }, [demoSessionEntry, sessionsVersion]);

  const filtersClearedRef = useRef(false);
  useEffect(() => {
    if (filtersClearedRef.current) {
      sessionsScrollRestoreRef.current = 0;
      if (desktopSessionsListRef.current) desktopSessionsListRef.current.scrollTop = 0;
      if (mobileSessionsListRef.current) mobileSessionsListRef.current.scrollTop = 0;
      filtersClearedRef.current = false;
    }
  }, [searchTerm, dateFilter, customDateRange]);

  useEffect(() => {
    if (!userContextProfile) return;
    const loadUserProfile = () => {
      try {
        setUserProfile(userContextProfile);
        const roleId = userContextProfile?.profile?.roleId || userContextProfile?.role;
        const roleName =
          userContextProfile?.profile?.roleName || userContextProfile?.roleName || roleId;
        let roleLabel = "user";
        if (roleId === 1 || roleName === "user") roleLabel = "user";
        if (roleId === 2 || roleName === "admin") roleLabel = "admin";
        if (roleId === 3 || roleName === "scribe_parttime") roleLabel = "scribe_parttime";
        if (roleId === 4 || roleName === "scribe_fulltime") roleLabel = "scribe_fulltime";
        setUserRole(roleLabel);
        const isScriberUser = roleId === 3 || roleId === 4 ||
          roleName === "scribe_parttime" || roleName === "scribe_fulltime";
        const isAdminUser = roleId === 2 || roleName === "admin" || roleName === "Administrator";
        const isFullTimeScribeUser = roleId === 4 || roleName === "scribe_fulltime";
        const isPartTimeScribeUser = roleId === 3 || roleName === "scribe_parttime";
        setIsScriber(isScriberUser);
        setIsAdmin(isAdminUser);
        setIsFullTimeScribe(isFullTimeScribeUser);
        setIsPartTimeScribe(isPartTimeScribeUser);
      } catch (error) { console.error(error); }
    };
    loadUserProfile();

    const uid = userContextProfile?.userId ?? userContextProfile?.userid ?? userContextProfile?.id;
    if (uid == null || uid === "") return;
    const bootstrapKey = String(uid);
    if (lastSessionsBootstrapKeyRef.current === bootstrapKey) return;
    lastSessionsBootstrapKeyRef.current = bootstrapKey;

    const roleId = userContextProfile?.profile?.roleId || userContextProfile?.role;
    const roleName = userContextProfile?.profile?.roleName || userContextProfile?.roleName || roleId;
    let roleLabel = "user";
    if (roleId === 1 || roleName === "user") roleLabel = "user";
    if (roleId === 2 || roleName === "admin") roleLabel = "admin";
    if (roleId === 3 || roleName === "scribe_parttime") roleLabel = "scribe_parttime";
    if (roleId === 4 || roleName === "scribe_fulltime") roleLabel = "scribe_fulltime";
    const isScriberUser = roleId === 3 || roleId === 4 ||
      roleName === "scribe_parttime" || roleName === "scribe_fulltime";
    const isAdminUser = roleId === 2 || roleName === "admin" || roleName === "Administrator";
    const isFullTimeScribeUser = roleId === 4 || roleName === "scribe_fulltime";
    const isPartTimeScribeUser = roleId === 3 || roleName === "scribe_parttime";

    const loadInitialSessions = async () => {
      const cached = readSessionsCache();
      const cachedSessions = filterVisibleSessions(cached?.sessions);
      if (!isAdminUser && cachedSessions.length) {
        setUserSessions(sortSessionsNewestFirst(cachedSessions));
        setSessionsOffset(typeof cached.sessionsOffset === "number" ? cached.sessionsOffset : cachedSessions.length);
        sessionsOffsetRef.current = typeof cached.sessionsOffset === "number" ? cached.sessionsOffset : cachedSessions.length;
        setHasMoreSessions(typeof cached.hasMoreSessions === "boolean" ? cached.hasMoreSessions : true);
        sessionsScrollRestoreRef.current = Number(cached.scrollTop || 0);
        loadedOffsetsRef.current = new Set(
          Array.isArray(cached.loadedOffsets) && cached.loadedOffsets.length ? cached.loadedOffsets : [0],
        );
        loadedSessionIdsRef.current = new Set(
          Array.isArray(cached.loadedSessionIds) && cached.loadedSessionIds.length
            ? cached.loadedSessionIds.map(String)
            : cachedSessions.map((s) => s?.id ?? s?.sessionId)
                .filter((id) => id != null && id !== "").map(String),
        );
        requestAnimationFrame(() => {
          const list = desktopSessionsListRef.current || mobileSessionsListRef.current;
          if (list) list.scrollTop = sessionsScrollRestoreRef.current;
        });
        setLoading(false);
        try {
          const response = await sessionService.getUserSessions({
            status: "all", limit: SESSIONS_PAGE_SIZE, offset: 0,
          });
          if (response?.success) {
            const fetchedSessions = sortSessionsNewestFirst(
              filterVisibleSessions((response.data?.sessions || []).map(normalizeSession)),
            );
            fetchedSessions.forEach((s) => {
              const sid = s?.id ?? s?.sessionId;
              if (sid != null && sid !== "") loadedSessionIdsRef.current.add(String(sid));
            });
            setUserSessions((prev) => mergeUniqueSessions(prev, fetchedSessions));
          }
        } catch (error) { console.error(error); }
        return;
      }
      try {
        setLoading(true);
        if (isAdminUser || roleLabel === "user" || isFullTimeScribeUser || isPartTimeScribeUser) {
          const response = await sessionService.getUserSessions({
            status: "all", limit: SESSIONS_PAGE_SIZE, offset: 0,
          });
          if (response.success) {
            const fetchedSessions = sortSessionsNewestFirst(
              filterVisibleSessions((response.data?.sessions || []).map(normalizeSession)),
            );
            setUserSessions((prev) => mergeUniqueSessions(prev, fetchedSessions));
            setHasMoreSessions(fetchedSessions.length >= SESSIONS_PAGE_SIZE);
            setSessionsOffset(fetchedSessions.length);
            sessionsOffsetRef.current = fetchedSessions.length;
            loadedOffsetsRef.current = new Set([0]);
            loadedSessionIdsRef.current = new Set(
              fetchedSessions.map((s) => s?.id ?? s?.sessionId)
                .filter((id) => id != null && id !== "").map(String),
            );
          }
        }
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    const loadAssignedSessionsInitial = async () => {
      if (!isScriberUser) return;
      try {
        setLoadingAssigned(true);
        const response = await adminService.getMyAssignedSOAPNotes();
        if (response.success) setAssignedSessions(response.data?.soapNotes || []);
        triggerAudioFetch().catch(() => {});
      } catch (error) { console.error(error); }
      finally { setLoadingAssigned(false); }
    };
    loadInitialSessions();
    loadAssignedSessionsInitial();
  }, [
    userContextProfile, SESSIONS_PAGE_SIZE, normalizeSession, mergeUniqueSessions,
    readSessionsCache, triggerAudioFetch,
  ]);

  const handleLoadMoreSessions = useCallback(async () => {
    if (!hasMoreSessions || isLoadingMoreSessionsRef.current || loading) return;
    const currentOffset = sessionsOffsetRef.current;
    const listBeforeFetch = desktopSessionsListRef.current || mobileSessionsListRef.current;
    const previousScrollTop = listBeforeFetch?.scrollTop ?? sessionsScrollRestoreRef.current ?? 0;
    const computeAnchor = (list) => {
      if (!list) return null;
      const containerTop = list.getBoundingClientRect().top;
      const rows = list.querySelectorAll("[data-session-id]");
      for (const row of rows) {
        const rowTop = row.getBoundingClientRect().top;
        if (rowTop >= containerTop - 1) {
          const sid = row.getAttribute("data-session-id");
          if (sid) return { sid, deltaTop: rowTop - containerTop };
        }
      }
      return null;
    };
    let anchor = computeAnchor(listBeforeFetch);
    isLoadingMoreSessionsRef.current = true;
    setIsLoadingMoreSessions(true);

    // The loading skeletons are rendered at the very bottom of the list, which is
    // usually just below the visible area. Once they render, scroll them into view
    // and re-capture the anchor so the final scroll position stays stable when the
    // real rows replace the skeletons.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!isLoadingMoreSessionsRef.current) return;
        const list = desktopSessionsListRef.current || mobileSessionsListRef.current;
        if (!list) return;
        list.scrollTop = list.scrollHeight;
        sessionsScrollRestoreRef.current = list.scrollTop;
        anchor = computeAnchor(list);
      });
    });
    try {
      const response = await sessionService.getUserSessions({
        status: "all", limit: SESSIONS_PAGE_SIZE, offset: currentOffset,
      });
      if (response.success) {
        loadedOffsetsRef.current.add(currentOffset);
        const fetchedSessions = filterVisibleSessions((response.data?.sessions || []).map(normalizeSession));
        fetchedSessions.forEach((s) => {
          const sid = s?.id ?? s?.sessionId;
          if (sid != null && sid !== "") loadedSessionIdsRef.current.add(String(sid));
        });
        setUserSessions((prev) => appendUniqueSessions(prev, fetchedSessions));
        const fetchedCount = fetchedSessions.length;
        const nextOffset = currentOffset + fetchedCount;
        setHasMoreSessions(fetchedCount >= SESSIONS_PAGE_SIZE);
        setSessionsOffset(nextOffset);
        sessionsOffsetRef.current = nextOffset;
        requestAnimationFrame(() => {
          const listAfterFetch = desktopSessionsListRef.current || mobileSessionsListRef.current;
          if (!listAfterFetch) return;
          let restoredTop = listAfterFetch.scrollTop || previousScrollTop;
          if (anchor?.sid) {
            const anchorRow = listAfterFetch.querySelector(`[data-session-id="${anchor.sid}"]`);
            if (anchorRow) {
              const containerTop = listAfterFetch.getBoundingClientRect().top;
              const currentDeltaTop = anchorRow.getBoundingClientRect().top - containerTop;
              const delta = currentDeltaTop - anchor.deltaTop;
              restoredTop = listAfterFetch.scrollTop + delta;
            }
          }
          listAfterFetch.scrollTop = restoredTop;
          sessionsScrollRestoreRef.current = restoredTop;
          writeSessionsCache({
            sessionsOffset: nextOffset,
            hasMoreSessions: fetchedCount >= SESSIONS_PAGE_SIZE,
            scrollTop: restoredTop,
            loadedOffsets: Array.from(loadedOffsetsRef.current),
          });
        });
      }
    } catch (error) { console.error(error); }
    finally {
      isLoadingMoreSessionsRef.current = false;
      setIsLoadingMoreSessions(false);
    }
  }, [hasMoreSessions, loading, SESSIONS_PAGE_SIZE, normalizeSession, appendUniqueSessions, writeSessionsCache]);

  // Filters/search run client-side over the loaded pages. While any are active the
  // (short) result list can't scroll, so the "Scroll down to load more" hint is hidden.
  const isFilteringActive =
    !!searchTerm ||
    dateFilter !== "all" ||
    creatorFilter !== "all" ||
    assigneeFilter !== "all" ||
    visitTypeFilter !== "all" ||
    genderFilter !== "all" ||
    !!customDateRange?.from ||
    !!customDateRange?.to;
  const refreshAdminSessions = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await sessionService.getUserSessions({
        status: "all", limit: SESSIONS_PAGE_SIZE, offset: 0,
      });
      if (!res?.success) return;
      const fresh = sortSessionsNewestFirst(
        filterVisibleSessions((res.data?.sessions || []).map(normalizeSession)),
      );
      if (!fresh.length) return;
      fresh.forEach((s) => {
        const sid = s?.id ?? s?.sessionId;
        if (sid != null && sid !== "") loadedSessionIdsRef.current.add(String(sid));
      });
      setUserSessions((prev) => mergeUniqueSessions(prev, fresh));
    } catch { /* silent */ }
  }, [isAdmin, SESSIONS_PAGE_SIZE, normalizeSession, mergeUniqueSessions]);

  useEffect(() => {
    if (!isAdmin) return;
    if (creatorFilter === "all") {
      creatorFilterRef.current = creatorFilter;
      return;
    }
    if (creatorFilterRef.current === creatorFilter) return;
    creatorFilterRef.current = creatorFilter;
    let cancelled = false;
    const loadCreatorSessions = async () => {
      try {
        const response = await sessionService.getUserSessions({
          status: "all",
          limit: 100,
          offset: 0,
          createdBy: creatorFilter,
        });
        if (cancelled || !response?.success) return;
        const fetchedSessions = sortSessionsNewestFirst(
          filterVisibleSessions((response.data?.sessions || []).map(normalizeSession)),
        );
        fetchedSessions.forEach((s) => {
          const sid = s?.id ?? s?.sessionId;
          if (sid != null && sid !== "") loadedSessionIdsRef.current.add(String(sid));
        });
        setUserSessions((prev) => mergeUniqueSessions(prev, fetchedSessions));
      } catch (error) {
        console.error(error);
      }
    };
    loadCreatorSessions();
    return () => { cancelled = true; };
  }, [creatorFilter, isAdmin, SESSIONS_PAGE_SIZE, normalizeSession, mergeUniqueSessions]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!showVisitsPanel && !mobileMenuOpen) return;
    let cancelled = false;
    const tick = () => { if (cancelled) return; void refreshAdminSessions(); };
    tick();
    const intervalId = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [isAdmin, showVisitsPanel, mobileMenuOpen, refreshAdminSessions]);

  useEffect(() => {
    if (!userContextProfile) {
      loadedOffsetsRef.current = new Set([0]);
      loadedSessionIdsRef.current = new Set();
    }
  }, [userContextProfile]);

  const handleSessionsScroll = useCallback((e) => {
    const target = e.currentTarget;
    lastUserScrollAtRef.current = Date.now();
    sessionsScrollRestoreRef.current = target.scrollTop;
    writeSessionsCache({ scrollTop: target.scrollTop });
    const canAutoLoadOwnSessions =
      (isAdmin || userRole === "user" || isFullTimeScribe || isPartTimeScribe) && sortedSessions.length > 0;
    if (
      !canAutoLoadOwnSessions ||
      isFilteringActive ||
      !hasMoreSessions ||
      isLoadingMoreSessionsRef.current ||
      loading
    ) return;
    if (target.scrollHeight - target.scrollTop - target.clientHeight <= 120) {
      handleLoadMoreSessions();
    }
  }, [
    isAdmin, userRole, isFullTimeScribe, isPartTimeScribe, sortedSessions.length,
    isFilteringActive, hasMoreSessions, loading, handleLoadMoreSessions, writeSessionsCache,
  ]);

  useLayoutEffect(() => {
    if (suppressScrollRestoreRef.current) return;
    const list = desktopSessionsListRef.current || mobileSessionsListRef.current;
    if (!list) return;
    const savedTop = sessionsScrollRestoreRef.current || 0;
    if (savedTop <= 0) return;
    if (Date.now() - lastUserScrollAtRef.current < 120) return;
    if (Math.abs(list.scrollTop - savedTop) > 24) {
      list.scrollTop = savedTop;
    }
  }, [userSessions.length, assignedSessions.length, resolvedActiveSession, loading, loadingAssigned]);

  useEffect(() => {
    if (suppressScrollRestoreRef.current) return;
    writeSessionsCache();
  }, [userSessions.length, resolvedActiveSession, writeSessionsCache]);

  const handleDeleteClick = (sessionId, e) => {
    e.stopPropagation();
    if (String(sessionId) === "demo-session") { setDeleteConfirm(sessionId); return; }
    if (shouldShowFeature("delete-session")) setDeleteConfirm(sessionId);
    else toast.error("You don't have permission to delete sessions");
  };
  const toggleSessionSelection = (sessionId, e) => {
    e.stopPropagation();
    setSelectedSessionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) newSet.delete(sessionId);
      else newSet.add(sessionId);
      return newSet;
    });
    if (!selectionMode && selectedSessionIds.size === 0) setSelectionMode(true);
  };
  useEffect(() => {
    if (selectedSessionIds.size === 0 && selectionMode) setSelectionMode(false);
  }, [selectedSessionIds, selectionMode]);

  // NOTE: previously this flipped the shared `loading` flag which made the
  // entire visits list flash skeletons and disabled the New Visit button
  // during a bulk delete. Now it uses its own `isBulkDeleting` flag.
  const confirmBulkDelete = useCallback(async () => {
    if (!bulkDeleteConfirm || bulkDeleteConfirm.length === 0) return;
    setIsBulkDeleting(true);
    try {
      const response = await sessionService.bulkDeleteSessions(bulkDeleteConfirm);
      const successfulIds = response?.data?.deleted || response?.body?.data?.deleted || bulkDeleteConfirm;
      setUserSessions((prev) => prev.filter((s) => !successfulIds.includes(s.id)));
      successfulIds.forEach((id) => purgeDeletedSessionFromCache(id));
      if (successfulIds.length && onDeleteSession) {
        await onDeleteSession(successfulIds[0], { skipApi: true, deletedIds: successfulIds });
      }
      if (successfulIds.length) toast.success(`Deleted ${successfulIds.length} session(s).`);
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error("Failed to delete sessions");
    } finally {
      setBulkDeleteConfirm(null);
      setSelectionMode(false);
      setSelectedSessionIds(new Set());
      setIsBulkDeleting(false);
    }
  }, [bulkDeleteConfirm, onDeleteSession, purgeDeletedSessionFromCache]);

  // NOTE: previously this flipped the shared `loading` flag which flashed
  // the visits list skeleton + disabled New Visit during a single delete.
  // Now it uses its own `isDeletingSession` flag.
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteConfirm === "demo-session") {
      if (typeof onDeleteDemoVisit === "function") onDeleteDemoVisit();
      setDeleteConfirm(null);
      return;
    }
    try {
      setIsDeletingSession(true);
      if (onDeleteSession) {
        await onDeleteSession(deleteConfirm);
        toast.success("Session deleted");
      } else {
        const response = await sessionService.deleteSession(deleteConfirm);
        if (response.success) {
          setUserSessions((prev) => prev.filter((s) => s.id !== deleteConfirm));
          purgeDeletedSessionFromCache(deleteConfirm);
          toast.success("Session deleted");
        }
      }
    } catch (error) { toast.error("Failed to delete session"); }
    finally { setIsDeletingSession(false); setDeleteConfirm(null); }
  };
  const cancelDelete = () => setDeleteConfirm(null);

  const handleEditClick = (sessionId, currentName, e) => {
    e.stopPropagation();
    if (!shouldShowFeature("edit-session")) { toast.error("No permission"); return; }
    if (!isAdmin && !isFullTimeScribe && userRole !== "user") {
      const session = userSessions.find((s) => s.id === sessionId);
      if (!session) { toast.error("Can only edit your own sessions"); return; }
    }
    setEditingSession(sessionId);
    setEditingName(currentName);
  };

  const handleSaveEdit = async (sessionId) => {
    if (!editingName.trim()) return;
    try {
      const response = await sessionService.updateSession(sessionId, { sessionName: editingName.trim() });
      if (response.success) {
        setUserSessions((prev) =>
          prev.map((s) => (s.id === sessionId ? { ...s, name: editingName.trim() } : s)),
        );
        if (onUpdateSessionName) onUpdateSessionName(sessionId, editingName.trim());
        toast.success("Session name updated");
      }
    } catch (error) { toast.error("Failed to update"); }
    finally { setEditingSession(null); setEditingName(""); }
  };
  const handleCancelEdit = () => { setEditingSession(null); setEditingName(""); };

  const handleSessionSelect = async (sessionId) => {
    if (String(sessionId) === "demo-session") {
      if (typeof onSelectDemoVisit === "function") onSelectDemoVisit();
      if (isMobile) setMobileMenuOpen(false);
      return;
    }
    const sid = String(sessionId);
    const now = Date.now();
    const currentSessionId = searchParams.get("sessionId");
    const isOnSessionView = location.pathname === "/scribe" && currentSessionId === sid;
    if (resolvedActiveSession != null && String(resolvedActiveSession) === sid) {
      if (isOnSessionView) { if (isMobile) setMobileMenuOpen(false); return; }
    }
    if (inFlightSessionSelectRef.current === sid) return;
    if (lastSessionSelectRef.current.id === sid && now - lastSessionSelectRef.current.ts < 500) return;
    lastSessionSelectRef.current = { id: sid, ts: now };
    inFlightSessionSelectRef.current = sid;
    try {
      localStorage.setItem("activeSessionId", sid);
      localStorage.setItem("lastSessionId", sid);
      if (onSelectSession) void onSelectSession(sid);
      navigate(`/scribe?sessionId=${sid}`);
    } finally { inFlightSessionSelectRef.current = null; }
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleAssignedSessionSelect = async (soapNoteId, sessionId) => {
    stopAllAudio();
    const assignedSession = assignedSessions.find((s) => s.soapNoteId === soapNoteId);
    const actualSessionId = assignedSession?.session?.sessionId || sessionId;
    localStorage.setItem("activeSessionId", actualSessionId);
    localStorage.setItem("activeSoapNoteId", soapNoteId);
    navigate(`/scribe?soapNoteId=${soapNoteId}&assigned=true`);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleBackToChart = () => navigate(`/scribe?sessionId=${resolvedActiveSession ?? activeSession}`);
  const handleKeyPress = (e, sessionId) => {
    if (e.key === "Enter") handleSaveEdit(sessionId);
    else if (e.key === "Escape") handleCancelEdit();
  };
  const handleLogout = async () => {
    localStorage.removeItem("activeSessionId");
    localStorage.removeItem("activeSoapNoteId");
    try {
      await performLogout();
      navigate("/signin", { replace: true });
    } catch (error) { toast.error("Failed to logout"); }
  };

  const scrollToActiveSession = useCallback(() => {
    if (!resolvedActiveSession) return;
    const sid = String(resolvedActiveSession);
    const list = desktopSessionsListRef.current || mobileSessionsListRef.current;
    if (!list) return;
    const row = list.querySelector(`[data-session-id="${sid}"]`);
    if (row) {
      const containerTop = list.getBoundingClientRect().top;
      const rowTop = row.getBoundingClientRect().top;
      // Leave room for the pinned date header so it doesn't cover the active card.
      const stickyHeader = row.closest("[data-date-group]")?.querySelector("[data-date-header]");
      const stickyOffset = stickyHeader ? stickyHeader.offsetHeight : 0;
      const targetScroll = Math.max(0, list.scrollTop + (rowTop - containerTop) - stickyOffset);
      list.scrollTop = targetScroll;
      sessionsScrollRestoreRef.current = targetScroll;
      suppressScrollRestoreRef.current = true;
      setTimeout(() => { suppressScrollRestoreRef.current = false; }, 0);
    }
  }, [resolvedActiveSession]);

  useLayoutEffect(() => {
    if (showVisitsPanel || mobileMenuOpen) {
      requestAnimationFrame(() => scrollToActiveSession());
    }
  }, [showVisitsPanel, mobileMenuOpen, resolvedActiveSession, scrollToActiveSession]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHelpDropdown && !event.target.closest(".help-dropdown") && !event.target.closest(".help-portal"))
        setShowHelpDropdown(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showHelpDropdown]);

  const getUserInitials = (profile) => {
    if (typeof profile === "string") {
      const email = profile;
      if (!email) return "DR";
      const parts = email.split("@")[0].split(".");
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return email.substring(0, 2).toUpperCase();
    }
    const firstName = profile?.profile?.firstName || profile?.firstName || "";
    const lastName = profile?.profile?.lastName || profile?.lastName || "";
    if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
    if (firstName) return firstName.substring(0, 2).toUpperCase();
    if (lastName) return lastName.substring(0, 2).toUpperCase();
    const email = profile?.email || profile?.profile?.email || "";
    if (!email) return "DR";
    const parts = email.split("@")[0].split(".");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return email.substring(0, 2).toUpperCase();
  };

  const avatarUrl = toAbsoluteAvatarUrl(
    userProfile?.profile?.profileImagePath ||
    userProfile?.profile?.profileimage ||
    userProfile?.profileImagePath || "",
  );
  const displayName =
    userProfile?.profile?.firstName + " " + userProfile?.profile?.lastName ||
    userProfile?.email || "Your account";
  const userRoleLabel = userProfile?.profile?.roleTitle || "User";

  const avatarInitials = useMemo(() => getUserInitials(userProfile || userContextProfile), [userProfile, userContextProfile]);
  const avatarUrlFinal = useMemo(() => avatarUrl, [avatarUrl]);
  const profileMenuItems = useMemo(() => [
    {
      label: "Profile",
      icon: <User className="w-4 h-4 mr-2" />,
      action: () => {
        setShowProfilePopup(false);
        if (isMobile) setMobileMenuOpen(false);
        navigate("/scribe/settings?tab=account");
      },
    },
    {
      label: "Logout",
      icon: <LogOut className="w-4 h-4 mr-2 text-red-500" />,
      action: handleLogout,
      className: "text-red-500 hover:text-red-600",
    },
  ], [isMobile, navigate, setMobileMenuOpen, handleLogout]);

  const handleCreateSession = async () => {
    const authRole = String(userContextProfile?.role || "").toLowerCase();
    const authRoleId = Number(userContextProfile?.profile?.roleId || userContextProfile?.roleId);
    const isAccountAdmin = isAdmin || userRole === "admin" || authRole.includes("admin");
    const isAccountDeveloper = userRole === "developer" || authRole.includes("developer") || authRoleId === 5;
    const isAccountScribe =
      isScriber || userRole === "scribe_parttime" || userRole === "scribe_fulltime" || authRole.includes("scribe");
    if (!shouldShowFeature("create-session")) {
      toast.error("You do not have permission to create sessions.");
      return;
    }
    if (isChartCreationLocked) {
      toast.error(getAccountLimitMessage());
      setShowSubscriptionModal(true);
      return;
    }
    try {
      const newSessionId = await onCreateSession();
      if (newSessionId) refreshTrialStatus();
      if (newSessionId) navigate(`/scribe?sessionId=${newSessionId}`);
      if (isMobile) setMobileMenuOpen(false);
    } catch (error) { toast.error("Failed to create session"); }
  };

  const handleCustomDateSelect = (range) => {
    if (range?.from) { setCustomDateRange(range); setDateFilter("custom"); }
    setShowCalendar(false);
  };
  const clearCustomDateRange = () => { setCustomDateRange({ from: null, to: null }); setDateFilter("all"); };
  const handleDropdownClick = (e) => e.stopPropagation();
  const handleSectionClick = (section) => setMobileSection(section);
  const handleBack = () => setMobileSection(null);

  useEffect(() => {
    if (showHelpDropdown && helpButtonRef.current) {
      const rect = helpButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 220;
      const padding = 8;
      let left = rect.right + padding;
      if (left + dropdownWidth > window.innerWidth - padding) left = rect.left - dropdownWidth - padding;
      const top = rect.top;
      setHelpDropdownPosition({ top, left });
    }
  }, [showHelpDropdown]);

  const HELP_LINKS = [
    { label: "Raise a Ticket", path: "/support-ticket", external: false },
    { label: "Help center", path: "https://virtualscribe.ai/help-center", external: true },
    { label: "Terms of use", path: "https://virtualscribe.ai/terms-of-use", external: true },
    { label: "Privacy policy", path: "https://virtualscribe.ai/privacy-policy", external: true },
  ];

  const displayLoading = loading;
  const showOwnSessions =
    (isAdmin || userRole === "user" || isFullTimeScribe || isPartTimeScribe || !!demoSessionEntry) &&
    sortedSessions.length > 0;
  const showAssignedSessions = sortedAssignedSessions.length > 0;
  const hasSessions = showOwnSessions || showAssignedSessions;
  const sessionTypeLabel = isPartTimeScribe
    ? "Assigned SOAP Notes"
    : isFullTimeScribe
      ? "My Sessions & Assigned Notes"
      : "Sessions";
  const noSessionsMessage = isPartTimeScribe
    ? "No SOAP notes assigned"
    : isFullTimeScribe
      ? "Start a new visit or wait for assigned notes"
      : "Start a new visit";

  const orderedSessions = useMemo(() => {
    if (!showOwnSessions) return [];
    return sortedSessions;
  }, [sortedSessions, showOwnSessions]);

  const sidebarNavItemClass =
    "w-full flex flex-col items-center gap-1 py-2 rounded-xl transition-all text-gray-700 hover:bg-gray-100";
  const sidebarNavLabelClass =
    "text-sm font-medium text-gray-800 text-center leading-tight px-0.5";

  // ═════════════════════════════════════════════════════════════════
  // 🚨 FIX: Single ref that carries ALL state + callbacks the inner
  // components need. Combined with `useMemo(() => fn, [])` on every
  // component below, this prevents React from treating them as new
  // component types on every SessionManager re-render.
  // ═════════════════════════════════════════════════════════════════
  const smRef = useRef(null);
  smRef.current = {
    // utils / callbacks
    getSessionDisplayName, getFormattedTime, getFormattedDateOnly, getSessionStatus,
    getSessionCreator, getAudioStatus, isActiveSessionId, handleBackToChart,
    handleKeyPress, handleSaveEdit, handleCancelEdit, handleSessionSelect,
    handleDeleteClick, handleAssignedSessionSelect, toggleSessionSelection,
    handleAudioPlay, handleSessionsScroll, shouldShowFeature,
    // setters
    setSearchTerm, setDateFilter, setCreatorFilter, setAssigneeFilter,
    setVisitTypeFilter, setGenderFilter, setCustomDateRange,
    setSelectionMode, setSelectedSessionIds, setBulkDeleteConfirm,
    setEditingName, setShowHelpDropdown,
    // state
    selectedSessionIds, selectionMode, editingSession, editingName,
    audioErrors, audioElements, playingAudio, loadingAudio,
    location, searchParams,
    isAdmin, isFullTimeScribe, isPartTimeScribe, userRole,
    displayLoading, showOwnSessions, showAssignedSessions,
    sortedSessions, sortedAssignedSessions,
    hasMoreSessions, isLoadingMoreSessions, noSessionsMessage, isFilteringActive,
    filtersClearedRef, parkedIdSet,
    searchTerm, dateFilter, visitTypeFilter, genderFilter,
    creatorFilter, assigneeFilter, customDateRange,
    isMappingAudio, trialStatus,
    helpDropdownPosition, onRestartTutorial, navigate,
    onDesktopSessionsListRef, onMobileSessionsListRef,
    userSessions, assignedSessions,
    uniqueCreators, uniqueAssignees, loadingAssignableUsers,
    visitTypeOptions, loadingVisitTypes,
    isMobile,
  };

  // ─────────────────────────────────────────────────────────────
  // STABLE COMPONENTS — defined once, read fresh state via smRef.
  // ─────────────────────────────────────────────────────────────
  // hide for now Will  add back later if needed
  // const AudioMappingNotice = useMemo(() => function AudioMappingNotice() {
  //   const { isMappingAudio } = smRef.current;
  //   if (!isMappingAudio) return null;
  //   return (
  //     <div className="mb-3 flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
  //       <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
  //       <span>Syncing audio…</span>
  //     </div>
  //   );
  // }, []);

  const AudioBlock = useMemo(() => function AudioBlock({ sid, showBackToChart = false }) {
    const sm = smRef.current;
    const audioStatus = sm.getAudioStatus(sid);
    const audioError = sm.audioErrors.get(sid);
    const isOnChartView =
      sm.location.pathname === "/scribe" && sm.searchParams.get("sessionId") === sid;
    if (!audioStatus.hasAudio) return null;
    return (
      <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
        {showBackToChart && sm.isActiveSessionId(sid) && !isOnChartView && (
          <button
            onClick={(e) => { e.stopPropagation(); sm.handleBackToChart(); }}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 rounded"
          >
            <ArrowLeft className="w-3 h-3" />
            <span>Back to Chart</span>
          </button>
        )}
        {audioStatus.isExpired && !audioError && (
          <div className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-500 rounded w-fit">
            <VolumeX className="w-3 h-3" />
            <span>Audio Expired</span>
          </div>
        )}
        {audioError && (
          <div className="space-y-1">
            <div className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded w-fit">
              <VolumeX className="w-3 h-3" />
              <span>Audio Error</span>
            </div>
            <div className="text-xs font-medium text-red-600 bg-red-50 p-2 rounded">
              {audioError}
            </div>
            <CorsInfo />
          </div>
        )}
        {audioStatus.isAccessible && (
          <AudioPlaybackControls
            audioElement={sm.audioElements.get(sid)}
            sessionId={sid}
            isPlaying={sm.playingAudio === sid}
            isLoading={sm.loadingAudio.has(sid)}
            onPlayPause={() => sm.handleAudioPlay(sid, { stopPropagation: () => {} })}
            compact={true}
          />
        )}
      </div>
    );
  }, []);

  const VisitItem = useMemo(() => function VisitItem({ session, isActive, isParked: isParkedVisit }) {
    const sm = smRef.current;
    const displayName = sm.getSessionDisplayName(session);
    const createdAtSource = session.createdAt || session.visitDate;
    const timeOnly = sm.getFormattedTime(createdAtSource);
    const createdDateOnly = createdAtSource
      ? new Date(createdAtSource).toLocaleDateString()
      : "--";
    const status = sm.getSessionStatus(session);
    const isSelected = sm.selectedSessionIds.has(session.id);
    const canModify =
      sm.isAdmin || sm.isFullTimeScribe || sm.isPartTimeScribe || sm.userRole === "user";
    const isEditing = sm.editingSession === session.id;
    // The demo visit is not a real session, so it can't be bulk-selected.
    const canSelect = canModify && !isEditing && !session.isDemoEntry;

    return (
      <div
        data-session-id={String(session.id)}
        className={`relative group rounded-2xl p-4 cursor-pointer transition-all duration-200 ease-out ${
          isSelected
            ? "bg-blue-50 ring-1 ring-blue-300 shadow-sm"
            : isActive ? "bg-gray-200 shadow-sm ring-1 ring-blue-200/80" : "hover:bg-white"
        } ${isParkedVisit && !isActive && !isSelected ? "ring-1 ring-amber-200 bg-amber-50/40" : ""}`}
        onClick={() => {
          if (isEditing) return;
          // While selecting, tapping the card toggles it (easier target than the checkbox).
          if (sm.selectionMode && canSelect) {
            sm.toggleSessionSelection(session.id, { stopPropagation: () => {} });
            return;
          }
          if (!sm.selectionMode) sm.handleSessionSelect(session.id);
        }}
      >
        <SessionCircularProgress sessionId={session?.sessionId || session?.id} />
        {canSelect && (
          <div
            className={`absolute top-3 left-3 z-10 transition-opacity ${
              sm.selectionMode
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => sm.toggleSessionSelection(session.id, e)}
              aria-label={`Select ${displayName}`}
              className="h-[18px] w-[18px] cursor-pointer rounded-md border-gray-300 bg-white text-blue-600 opacity-60 shadow-sm transition-opacity checked:opacity-100 hover:opacity-100 focus:opacity-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
            />
          </div>
        )}
        <div className={canSelect ? "pl-7" : ""}>
          <div className="flex justify-between items-start gap-2">
            {isEditing ? (
              <div className="flex items-center gap-2 w-full" onClick={(e) => e.stopPropagation()}>
                <input
                  autoFocus
                  type="text"
                  value={sm.editingName}
                  onChange={(e) => sm.setEditingName(e.target.value)}
                  onKeyDown={(e) => sm.handleKeyPress(e, session.id)}
                  onBlur={() => sm.handleSaveEdit(session.id)}
                  className="flex-1 text-sm border border-blue-400 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                <button
                  onMouseDown={(e) => { e.preventDefault(); sm.handleSaveEdit(session.id); }}
                  className="text-sm px-3 py-1.5 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg hover:shadow-lg transition"
                >Save</button>
                <button
                  onMouseDown={(e) => { e.preventDefault(); sm.handleCancelEdit(); }}
                  className="text-sm px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                >Cancel</button>
              </div>
            ) : (
              <>
                <h4 className="text-base font-medium text-gray-900 truncate flex-1 min-w-0">
                  {displayName}
                </h4>
                {isParkedVisit && (
                  <span
                    className="flex-shrink-0 text-xs font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700"
                    title="Capture parked — open to resume"
                  >Parked</span>
                )}
                {canModify && !sm.selectionMode && (
                  <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                    <button
                      onClick={(e) => sm.handleDeleteClick(session.id, e)}
                      className="p-1 rounded-full text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          {!isEditing && (
            <>
              <p className="text-sm font-medium text-gray-500">
                {timeOnly}
                {status.label !== "Not started" && ` — ${status.label}`}
              </p>
              <p className="text-sm font-medium text-gray-500 mt-1">
                <Calendar className="inline w-4 h-4 mr-1" /> {createdDateOnly}
              </p>
              {sm.isAdmin && (
                <div className="mt-2 text-sm font-medium text-gray-500 space-y-1">
                  {sm.getSessionCreator(session) && (
                  <div className="truncate" title={sm.getSessionCreator(session)}>
                    Created by: {sm.getSessionCreator(session)}
                  </div>
                  )}
                </div>
              )}
              <AudioBlock sid={session.id} showBackToChart={false} />
            </>
          )}
        </div>
      </div>
    );
  }, [AudioBlock]);

  const AssignedSessionCard = useMemo(() => function AssignedSessionCard({ item }) {
    const sm = smRef.current;
    const session = item.session;
    const patient = item.patient;
    const sessionId = session?.sessionId;
    const appointmentDate = session?.visitDate
      ? new Date(session.visitDate).toLocaleDateString()
      : "Unknown date";
    const appointmentTime = session?.visitDate
      ? new Date(session.visitDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "";
    const isActive = sm.isActiveSessionId(sessionId);

    return (
      <div
        data-session-id={sessionId != null ? String(sessionId) : ""}
        className={`relative group rounded-2xl p-4 cursor-pointer transition-all duration-200 ease-out ${
          isActive ? "bg-blue-50 shadow-sm ring-1 ring-blue-200/70" : "hover:bg-white"
        }`}
        onClick={() => sm.handleAssignedSessionSelect(item.soapNoteId, sessionId)}
      >
        <SessionCircularProgress sessionId={sessionId} />
        <div className="flex justify-between items-start gap-2">
          <div>
            <h4 className="text-sm font-medium text-gray-900">
              {patient?.firstName} {patient?.lastName}
            </h4>
            <p className="text-sm text-gray-500">
              {appointmentTime} — {item.status || "Assigned"}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              <Calendar className="inline w-4 h-4 mr-1" /> {appointmentDate}
            </p>
          </div>
          {item.status && (
            <Badge variant="outline" className="mt-1 text-sm">{item.status}</Badge>
          )}
        </div>
        <AudioBlock sid={sessionId} showBackToChart={false} />
      </div>
    );
  }, [AudioBlock]);

  const SessionListContent = useMemo(() => function SessionListContent({
    scrollRef, onScroll, noScrollContainer,
  }) {
    const sm = smRef.current;
    const demoEntries = sm.isAdmin ? [] : sm.sortedSessions.filter(s => s.isDemoEntry);
    const regularSessions = sm.sortedSessions.filter(s => !s.isDemoEntry);
    const groupedSessions = regularSessions.length === 0 ? {} : groupSessionsByDate(regularSessions);

    const content = (
      <div className="space-y-6">
        {demoEntries.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-600 px-2 py-1.5">Demo Visit</h3>
            <div className="space-y-3">
              {demoEntries.map((session) => (
                <VisitItem
                  key={session.id}
                  session={session}
                  isActive={sm.isActiveSessionId(session.id)}
                  isParked={sm.parkedIdSet.has(String(session.id))}
                />
              ))}
            </div>
          </div>
        )}

        {sm.displayLoading ? (
          <SessionListSkeleton count={6} />
        ) : Object.keys(groupedSessions).length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {sm.searchTerm || sm.dateFilter !== "all" ||
             sm.visitTypeFilter !== "all" || sm.genderFilter !== "all" ? (
              <>
                <p className="text-sm">No matching visits found</p>
                <Button
                  variant="outline" size="sm" className="mt-2"
                  onClick={() => {
                    sm.setSearchTerm(""); sm.setDateFilter("all");
                    sm.setCreatorFilter("all"); sm.setAssigneeFilter("all");
                    sm.setVisitTypeFilter("all"); sm.setGenderFilter("all");
                    sm.setCustomDateRange({ from: null, to: null });
                  }}
                >Clear filters</Button>
              </>
            ) : (
              <>
                <p className="text-sm">No visits yet</p>
                <p className="text-xs mt-1">{sm.noSessionsMessage}</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedSessions).map(([dateKey, sessions]) => (
              <div key={dateKey} data-date-group className="space-y-2">
                <h3 data-date-header className="sticky top-0 z-20 -mx-1.5 bg-[#f8f8fc] px-3.5 py-1.5 text-sm font-semibold text-gray-600">
                  {dateKey}
                </h3>
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <VisitItem
                      key={session.id}
                      session={session}
                      isActive={sm.isActiveSessionId(session.id)}
                      isParked={sm.parkedIdSet.has(String(session.id))}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {sm.selectionMode && sm.selectedSessionIds.size > 0 && (() => {
          // Only act on what the user can actually see — selections hidden by a
          // later search/filter must never be deleted by accident.
          const visibleIds = regularSessions.map((s) => s.id);
          const selectedVisibleIds = visibleIds.filter((id) => sm.selectedSessionIds.has(id));
          const allVisibleSelected =
            visibleIds.length > 0 && selectedVisibleIds.length === visibleIds.length;
          if (selectedVisibleIds.length === 0) return null;
          return (
            <div className="sticky bottom-0 z-20 -mx-1 rounded-2xl border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur">
              <div className={sm.isMobile ? "flex flex-col gap-2.5" : "flex items-center justify-between gap-2"}>
                <div className={`flex min-w-0 ${sm.isMobile ? "items-center justify-between gap-2" : "flex-col"}`}>
                  <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {selectedVisibleIds.length} selected
                  </span>
                  <button
                    type="button"
                    className={`text-xs font-medium text-blue-600 hover:underline ${
                      sm.isMobile ? "shrink-0 whitespace-nowrap text-right" : "text-left"
                    }`}
                    onClick={() =>
                      sm.setSelectedSessionIds(allVisibleSelected ? new Set() : new Set(visibleIds))
                    }
                  >
                    {allVisibleSelected ? "Deselect all" : `Select all (${visibleIds.length})`}
                  </button>
                </div>
                <div className={sm.isMobile ? "flex w-full gap-2" : "flex flex-shrink-0 gap-2"}>
                  <Button
                    variant="outline" size="sm"
                    className={sm.isMobile ? "min-w-0 flex-1 justify-center whitespace-nowrap px-2" : undefined}
                    onClick={() => {
                      sm.setSelectionMode(false);
                      sm.setSelectedSessionIds(new Set());
                    }}
                  >Cancel</Button>
                  <Button
                    size="sm"
                    className={`bg-red-600 text-white hover:bg-red-700 ${
                      sm.isMobile ? "min-w-0 flex-1 justify-center gap-1 whitespace-nowrap px-2" : ""
                    }`}
                    onClick={() => sm.setBulkDeleteConfirm(selectedVisibleIds)}
                  >
                    <Trash2 className={`${sm.isMobile ? "" : "mr-1.5"} h-3.5 w-3.5`} />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {sm.showOwnSessions && sm.hasMoreSessions && (
          <div className="pt-3 pb-1 space-y-3">
            {sm.isLoadingMoreSessions ? (
              <>
                <SessionSkeletonRow />
                <SessionSkeletonRow />
                <SessionSkeletonRow />
                <div className="flex items-center justify-center gap-2 pt-1 text-xs text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                  <span>Loading more visits…</span>
                </div>
              </>
            ) : sm.isFilteringActive ? null : (
              <div className="flex justify-center text-xs text-gray-500">
                <span>Scroll down to load more</span>
              </div>
            )}
          </div>
        )}
      </div>
    );

    if (noScrollContainer) return content;

    return (
      <div
        ref={scrollRef || sm.onDesktopSessionsListRef}
        onScroll={onScroll || sm.handleSessionsScroll}
        style={{ maxHeight: "calc(100% - 12.5rem)", overflowY: "auto" }}
      >
        {content}
      </div>
    );
  }, [VisitItem]);

  const SubscriptionStatusBlock = useMemo(() => function SubscriptionStatusBlock({ compact = false }) {
    const sm = smRef.current;
    const { trialStatus, shouldShowFeature } = sm;
    if (!shouldShowFeature("subscription")) return null;

    const isPremium = trialStatus.planType !== "free";
    const planKey = String(trialStatus.planKey || "").toLowerCase();
    const isStarter = isPremium && planKey === "starter";
    const planLabel =
      isStarter ? "Starter"
      : planKey === "elite" ? "Elite"
      : planKey === "professional" ? "Professional"
      : isPremium ? "Premium" : null;
    const used = trialStatus.chartsUsed || 0;
    const total = isStarter
      ? trialStatus.maxChartsPerMonth || trialStatus.maxCharts || 50
      : trialStatus.maxChartsPerDay || trialStatus.maxCharts || 25;
    const displayUsed = Math.min(used, total);
    const daysLeft = trialStatus.daysRemaining;
    const subscriptionDaysLeft = trialStatus.subscriptionDaysRemaining;
    const subscriptionEnd = trialStatus.subscriptionEnd;
    const pct = total > 0 ? Math.min(100, (displayUsed / total) * 100) : 0;

    const formatEndDate = (value) => {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    };

    if (compact) {
      if (isPremium && !isStarter) {
        return (
          <div className="w-full flex flex-col items-center gap-1 py-2 px-1">
            <Crown className="w-4 h-4 text-yellow-600 shrink-0" />
            <span className="text-xs font-semibold text-gray-800 leading-none">{planLabel}</span>
            {subscriptionDaysLeft != null && subscriptionDaysLeft >= 0 && (
              <span className="text-xs font-medium text-gray-600 leading-none text-center">
                {subscriptionDaysLeft}d left
              </span>
            )}
          </div>
        );
      }
      if (isStarter) {
        return (
          <div className="w-full flex flex-col items-center gap-1.5 py-2 px-1">
            <Crown className="w-4 h-4 text-slate-600 shrink-0" />
            <span className="text-xs font-semibold text-gray-800 leading-none">Starter</span>
            <span className="text-xs font-medium text-gray-700 leading-none">{displayUsed}/{total}</span>
            <div className="w-full max-w-[52px] bg-gray-200 rounded-full h-1">
              <div className="bg-gradient-to-r from-blue-500 to-teal-500 h-1 rounded-full transition-all"
                style={{ width: `${pct}%` }} />
            </div>
            {!trialStatus.canCreateChart && (
              <span className="text-xs font-semibold text-red-600 leading-none">Limit reached</span>
            )}
          </div>
        );
      }
      return (
        <div className="w-full flex flex-col items-center gap-1.5 py-2 px-1">
          <Zap className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="text-xs font-medium text-gray-700 leading-none">{displayUsed}/{total}</span>
          <div className="w-full max-w-[52px] bg-gray-200 rounded-full h-1">
            <div className="bg-gradient-to-r from-blue-500 to-teal-500 h-1 rounded-full transition-all"
              style={{ width: `${pct}%` }} />
          </div>
          {daysLeft != null && daysLeft >= 0 && (
            <span className="text-xs font-medium text-gray-600 leading-none">{daysLeft}d left</span>
          )}
          {!trialStatus.canCreateChart && (
            <span className="text-xs font-semibold text-red-600 leading-none">Limit reached</span>
          )}
        </div>
      );
    }

    if (isPremium) {
      const endLabel = formatEndDate(subscriptionEnd);
      return (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-3 border border-amber-100/80">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <Crown className="w-4 h-4 text-yellow-600 shrink-0" />
              <span className="text-sm font-medium text-gray-900 truncate">{planLabel}</span>
            </div>
            <Badge variant="secondary"
              className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 bg-emerald-100 text-emerald-700">
              Active
            </Badge>
          </div>
          <div className="space-y-1.5 text-xs text-gray-600">
            {isStarter && (
              <>
                <div className="flex justify-between gap-2">
                  <span>Notes this month</span>
                  <span className="font-medium text-gray-800">{displayUsed}/{total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-gradient-to-r from-blue-500 to-teal-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${pct}%` }} />
                </div>
                <div className="flex justify-between gap-2">
                  <span>Note history</span>
                  <span className="font-medium text-gray-800">30 days</span>
                </div>
                {!trialStatus.canCreateChart && (
                  <p className="text-xs font-semibold text-red-600">
                    Monthly note limit reached — upgrade for unlimited
                  </p>
                )}
              </>
            )}
            {endLabel && (
              <div className="flex justify-between gap-2">
                <span>Renewed on</span>
                <span className="font-medium text-gray-800">{endLabel}</span>
              </div>
            )}
            {subscriptionDaysLeft != null && subscriptionDaysLeft >= 0 && (
              <div className="flex justify-between gap-2">
                <span>Days remaining</span>
                <span className="font-medium text-gray-800">{subscriptionDaysLeft}</span>
              </div>
            )}
            {trialStatus.billingPeriodLabel && (
              <div className="flex justify-between gap-2">
                <span>Billing</span>
                <span className="font-medium text-gray-800">{trialStatus.billingPeriodLabel}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-3 border border-blue-100/80">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Zap className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="text-sm font-medium text-gray-900 truncate">Free Trial</span>
          </div>
          <Badge
            variant={trialStatus.canCreateChart !== false ? "secondary" : "destructive"}
            className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${
              trialStatus.canCreateChart !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-500 text-white"
            }`}
          >
            {trialStatus.canCreateChart !== false ? "Active" : "Limit reached"}
          </Badge>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Charts today</span>
            <span>{displayUsed}/{total}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-gradient-to-r from-blue-500 to-teal-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${pct}%` }} />
          </div>
          {daysLeft != null && daysLeft >= 0 && (
            <div className="flex justify-between text-xs text-gray-600">
              <span>Trial days left</span>
              <span>{daysLeft}</span>
            </div>
          )}
        </div>
      </div>
    );
  }, []);

  const chartCreationTooltip = useMemo(() => {
    if (!(isChartCreationLocked && !isLimitExempt)) return "";
    const trialExpired =
      trialStatus.accessReason === "TRIAL_PERIOD_EXPIRED" ||
      (trialStatus.daysRemaining === 0 && trialStatus.canUsePlatform === false);
    if (trialExpired) return "Your trial has ended. Please upgrade your plan.";
    const now = new Date();
    const nextDay = new Date(now);
    nextDay.setHours(24, 0, 0, 0);
    const diffMs = Math.max(0, nextDay.getTime() - now.getTime());
    const totalMinutes = Math.ceil(diffMs / (60 * 1000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `Daily chart limit reached. You can create a new chart in ${hours}h ${minutes}m.`;
  }, [isChartCreationLocked, isLimitExempt, trialStatus]);

  const FilterPopupContent = useMemo(() => function FilterPopupContent() {
    const sm = smRef.current;
    const [creatorOpen, setCreatorOpen] = useState(false);
    const [visitTypeOpenLocal, setVisitTypeOpenLocal] = useState(false);
    const [genderOpenLocal, setGenderOpenLocal] = useState(false);

    const formatDateForInput = (date) => {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0];
    };

    const handleStartDateChange = (e) => {
      const val = e.target.value;
      if (val) {
        sm.setCustomDateRange((prev) => ({ ...prev, from: new Date(val) }));
        sm.setDateFilter("custom");
      } else {
        sm.setCustomDateRange((prev) => ({ ...prev, from: null }));
        if (!sm.customDateRange.to) sm.setDateFilter("all");
      }
    };

    const handleEndDateChange = (e) => {
      const val = e.target.value;
      if (val) {
        sm.setCustomDateRange((prev) => ({ ...prev, to: new Date(val) }));
        sm.setDateFilter("custom");
      } else {
        sm.setCustomDateRange((prev) => ({ ...prev, to: null }));
        if (!sm.customDateRange.from) sm.setDateFilter("all");
      }
    };

    const creatorOptions = [
      { value: "all", label: "All Creators" },
      ...sm.uniqueCreators,
    ];

    const assigneeOptions = [
      { value: "all", label: "All Assignees" },
      ...(sm.loadingAssignableUsers
        ? [{ value: "loading", label: "Loading..." }]
        : sm.uniqueAssignees.map((a) => ({ value: a, label: a }))),
    ];

    const visitTypeOptionsList = (() => {
      const types = Array.isArray(sm.visitTypeOptions) ? sm.visitTypeOptions : [];
      return [
        { value: "all", label: "All Types" },
        ...types.map((type) => {
          let typeValue = type, typeLabel = type;
          if (typeof type === "object" && type !== null) {
            typeValue = type.value || type.id || type.name || JSON.stringify(type);
            typeLabel = type.label || type.name || typeValue;
          } else if (typeof type === "string") { typeValue = type; typeLabel = type; }
          else { typeValue = String(type); typeLabel = String(type); }
          const formattedLabel = String(typeLabel).split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
          return { value: typeValue, label: formattedLabel };
        }),
      ];
    })();

    const genderOptionsList = [
      { value: "all", label: "All Genders" },
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "other", label: "Other" },
    ];

    const clearAll = () => {
      sm.setDateFilter("all"); sm.setCreatorFilter("all"); sm.setAssigneeFilter("all");
      sm.setVisitTypeFilter("all"); sm.setGenderFilter("all");
      sm.setCustomDateRange({ from: null, to: null });
      sm.filtersClearedRef.current = true;
    };

    if (sm.isMobile) {
      return (
        <div className="w-full px-5 pb-5 pt-2 space-y-5" onClick={handleDropdownClick}>
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Date Range</label>
            <select value={sm.dateFilter}
              onChange={(e) => {
                const val = e.target.value;
                sm.setDateFilter(val);
                if (val !== "custom") sm.setCustomDateRange({ from: null, to: null });
              }}
              className="w-full h-10 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-500">
              <option value="all">All</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7">This week</option>
              <option value="last30">Last week</option>
              <option value="custom">Custom range</option>
            </select>
          </div>

          {sm.dateFilter === "custom" && (
            <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600 block mb-1">Start</label>
                <input type="date" value={formatDateForInput(sm.customDateRange.from)}
                  onChange={handleStartDateChange}
                  className="w-full min-w-0 h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">End</label>
                <input type="date" value={formatDateForInput(sm.customDateRange.to)}
                  onChange={handleEndDateChange}
                  className="w-full min-w-0 h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          )}

          {sm.isAdmin && (
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Created by</label>
              <FilterSelect
                value={sm.creatorFilter}
                onChange={sm.setCreatorFilter}
                options={creatorOptions}
                placeholder="Filter by creator"
                open={creatorOpen}
                setOpen={setCreatorOpen}
                onWillOpen={() => {
                  setVisitTypeOpenLocal(false);
                  setGenderOpenLocal(false);
                }}
              />
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-1.5">Type of Visit</label>
            <select value={sm.visitTypeFilter}
              onChange={(e) => sm.setVisitTypeFilter(e.target.value)}
              className="w-full h-10 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-500">
              {visitTypeOptionsList.map((opt) => (<option key={String(opt.value)} value={opt.value}>{opt.label}</option>))}
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-1.5">Gender</label>
            <select value={sm.genderFilter}
              onChange={(e) => sm.setGenderFilter(e.target.value)}
              className="w-full h-10 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:ring-2 focus:ring-blue-500">
              {genderOptionsList.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
            </select>
          </div>

          {(sm.dateFilter !== "all" || sm.creatorFilter !== "all" || sm.assigneeFilter !== "all" ||
            sm.visitTypeFilter !== "all" || sm.genderFilter !== "all" ||
            sm.customDateRange.from || sm.customDateRange.to) && (
            <Button variant="outline" size="sm"
              className="w-full h-10 text-sm font-medium text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 mt-2"
              onClick={clearAll}>Clear all filters</Button>
          )}
        </div>
      );
    }

   return (
  <div className="w-full px-4 pb-4 pt-3" onClick={handleDropdownClick}>
    {/* Date Range — compact 3-col chips */}
    <div className="mb-4">
      <label className="text-sm font-semibold text-gray-700 block mb-2">
        Date Range
      </label>
      <div className="grid grid-cols-3 gap-2">
        {[
          { value: "all", label: "All" },
          { value: "today", label: "Today" },
          { value: "yesterday", label: "Yesterday" },
          { value: "last7", label: "This week" },
          { value: "last30", label: "Last week" },
          { value: "custom", label: "Custom" },
        ].map((option) => (
          <button
            key={option.value}
            onClick={() => {
              sm.setDateFilter(option.value);
              if (option.value !== "custom") {
                sm.setCustomDateRange({ from: null, to: null });
              }
            }}
            className={`h-8 px-1 text-xs font-medium rounded-lg border transition-colors ${
              sm.dateFilter === option.value
                ? "bg-blue-50 border-blue-300 text-blue-700"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Custom range inputs — only when needed */}
      {sm.dateFilter === "custom" && (
        <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-2 mt-2.5">
          <div>
            <label className="text-sm text-gray-600 block mb-1">From</label>
            <input
              type="date"
              value={formatDateForInput(sm.customDateRange.from)}
              onChange={handleStartDateChange}
              className="w-full min-w-0 h-10 text-sm border border-gray-200 rounded-lg px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600 block mb-1">To</label>
            <input
              type="date"
              value={formatDateForInput(sm.customDateRange.to)}
              onChange={handleEndDateChange}
              className="w-full min-w-0 h-10 text-sm border border-gray-200 rounded-lg px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}
    </div>

    {/* Other filters — responsive grid */}
    <div className="grid grid-cols-2 gap-x-3 gap-y-3">
      {sm.isAdmin && (
        <div className="col-span-2">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">
            Created by
          </label>
          <FilterSelect
            value={sm.creatorFilter}
            onChange={sm.setCreatorFilter}
            options={creatorOptions}
            placeholder="Filter by creator"
            open={creatorOpen}
            setOpen={setCreatorOpen}
            onWillOpen={() => {
              setVisitTypeOpenLocal(false);
              setGenderOpenLocal(false);
            }}
          />
        </div>
      )}

      <div className="col-span-2">
        <label className="text-sm font-semibold text-gray-700 block mb-1.5">
          Visit Type
        </label>
        <FilterSelect
          value={sm.visitTypeFilter}
          onChange={sm.setVisitTypeFilter}
          options={visitTypeOptionsList}
          placeholder="Visit type"
          open={visitTypeOpenLocal}
          setOpen={setVisitTypeOpenLocal}
          onWillOpen={() => {
            setCreatorOpen(false);
            setGenderOpenLocal(false);
          }}
        />
      </div>

      <div className="col-span-2">
        <label className="text-sm font-semibold text-gray-700 block mb-1.5">
          Gender
        </label>
        <FilterSelect
          value={sm.genderFilter}
          onChange={sm.setGenderFilter}
          options={genderOptionsList}
          placeholder="Gender"
          open={genderOpenLocal}
          setOpen={setGenderOpenLocal}
          onWillOpen={() => {
            setCreatorOpen(false);
            setVisitTypeOpenLocal(false);
          }}
        />
      </div>
    </div>

    {/* Clear all — full width, only when filters active */}
    {(sm.dateFilter !== "all" ||
      sm.creatorFilter !== "all" ||
      sm.assigneeFilter !== "all" ||
      sm.visitTypeFilter !== "all" ||
      sm.genderFilter !== "all" ||
      sm.customDateRange.from ||
      sm.customDateRange.to) && (
      <Button
        variant="outline"
        size="sm"
        className="w-full h-10 text-sm font-medium text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 mt-4"
        onClick={clearAll}
      >
        Clear all filters
      </Button>
    )}
  </div>
);
  }, []);

  // FilterPopupContent reads its values from smRef, so the element must be
  // re-created whenever filter state changes; otherwise the memoized
  // SearchFilterRow never re-renders it and the selected Date Range chip
  // "Clear all filters" button stay stale.
  const filterContent = useMemo(
    () => <FilterPopupContent />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      FilterPopupContent,
      dateFilter,
      customDateRange,
      creatorFilter,
      assigneeFilter,
      visitTypeFilter,
      genderFilter,
      uniqueCreators,
      uniqueAssignees,
      loadingAssignableUsers,
      visitTypeOptions,
      isAdmin,
      isMobile,
    ],
  );

  const hasActiveFilters =
    dateFilter !== "all" ||
    creatorFilter !== "all" ||
    assigneeFilter !== "all" ||
    visitTypeFilter !== "all" ||
    genderFilter !== "all" ||
    !!customDateRange?.from ||
    !!customDateRange?.to;

  const handleClearFilters = useCallback(() => {
    setDateFilter("all");
    setCreatorFilter("all");
    setAssigneeFilter("all");
    setVisitTypeFilter("all");
    setGenderFilter("all");
    setCustomDateRange({ from: null, to: null });
    filtersClearedRef.current = true;
  }, []);

  const HelpPopup = useMemo(() => function HelpPopup() {
    const sm = smRef.current;
    return (
      <div className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999]"
        style={{ top: sm.helpDropdownPosition.top, left: sm.helpDropdownPosition.left, width: "220px" }}>
        <div className="py-2">
          {HELP_LINKS.map((link) => (
            <button key={link.path}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => {
                sm.setShowHelpDropdown(false);
                if (link.external) window.open(link.path, "_blank", "noopener,noreferrer");
                else sm.navigate(link.path);
              }}>{link.label}</button>
          ))}
          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              sm.setShowHelpDropdown(false);
              if (sm.onRestartTutorial) sm.onRestartTutorial();
            }}>Product Tour</button>
        </div>
      </div>
    );
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Layouts
  // ─────────────────────────────────────────────────────────────
  const desktopLayout = showVisitsPanel ? (
    <div className="bg-[#f8f8fc] border-r border-gray-200 h-full pl-0 w-[300px] lg:w-[340px] xl:w-[380px] flex-shrink-0">
      <div className="p-6 pt-6 pb-4 flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between mb-6">
          <TooltipProvider delayDuration={0} skipDelayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <button
                    ref={assignNewVisitButtonRef}
                    data-tour="new-visit"
                    data-testid="new-visit-button"
                    onClick={handleCreateSession}
                    disabled={loading || isCreatingSession || (isChartCreationLocked && !isLimitExempt)}
                    className="bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-2xl px-5 py-3 shadow-sm flex items-center gap-2 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition">
                    {loading || isCreatingSession ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        {isChartCreationLocked && !isLimitExempt && (
                          <Lock size={18} className="shrink-0 text-white/70" />
                        )}
                        <Plus size={20} />
                      </>
                    )}{" "}New visit
                  </button>
                </span>
              </TooltipTrigger>
              {chartCreationTooltip ? (
                <TooltipContent side="right" align="center" sideOffset={10}
                  className="max-w-[280px] whitespace-normal shadow-xl backdrop-blur-none"
                  style={{ backgroundColor: "#2b2438", border: "1px solid #4b3f67", color: "#f5f3ff", opacity: 1 }}>
                  {chartCreationTooltip}
                </TooltipContent>
              ) : null}
            </Tooltip>
          </TooltipProvider>
          <button onClick={handleClosePanel} className="p-2 rounded-xl hover:bg-white">
            <ChevronsLeft size={22} />
          </button>
        </div>

        <SearchFilterRow
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterPopoverOpen={filterPopoverOpen}
          setFilterPopoverOpen={setFilterPopoverOpen}
          filterContent={filterContent}
          isSyncingAudio={isMappingAudio && hasRealSessions}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
        />
        {/* <AudioMappingNotice /> */}

        <div ref={onDesktopSessionsListRef} onScroll={handleSessionsScroll}
          className="flex-1 min-h-0 overflow-y-auto pl-1.5 pr-1.5">
          {showAssignedSessions && (
            <>
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Assigned SOAP Notes</h2>
              <div className="space-y-3 mb-8">
                {sortedAssignedSessions.map((item) => (
                  <AssignedSessionCard key={item.soapNoteId} item={item} />
                ))}
              </div>
            </>
          )}

          {showOwnSessions && (
            <>
              <h2 className="text-sm font-semibold text-gray-700 mb-3">All Visits</h2>
              <SessionListContent noScrollContainer />
            </>
          )}

          {!displayLoading &&
            !(showAssignedSessions && sortedAssignedSessions.length > 0) &&
            !(showOwnSessions && sortedSessions.length > 0) &&
            (searchTerm || dateFilter !== "all" || creatorFilter !== "all" ||
              assigneeFilter !== "all" || visitTypeFilter !== "all" || genderFilter !== "all") && (
              <div className="text-center py-12 text-gray-500">
                <p className="text-sm">No matching visits found</p>
                <Button variant="outline" size="sm" className="mt-3"
                  onClick={() => {
                    setSearchTerm(""); setDateFilter("all");
                    setCreatorFilter("all"); setAssigneeFilter("all");
                    setVisitTypeFilter("all"); setGenderFilter("all");
                    setCustomDateRange({ from: null, to: null });
                    filtersClearedRef.current = true;
                  }}>Clear all filters</Button>
              </div>
            )}
        </div>
      </div>
    </div>
  ) : null;

  const mobileLayout = mobileMenuOpen ? (
    <div className="fixed inset-0 z-40 flex md:hidden">
      <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
      <div className="relative flex h-full w-[85%] max-w-[400px] bg-white shadow-xl overflow-hidden">
        {/* Left icon bar */}
        <div className="w-[80px] min-w-[80px] bg-white border-r border-gray-200 flex flex-col justify-between py-4 flex-shrink-0 overflow-y-auto">
          <div>
            <div className="space-y-5 px-2">
              {shouldShowFeature("analytics") && (
                <button onClick={() => {
                  navigate({ pathname: "/admin/analytics", search: "" });
                  setSessionVisible(false); setIsHoverLocked(false); setMobileMenuOpen(false);
                }}
                  className={`${sidebarNavItemClass} ${isAnalyticsRoute ? "text-blue-600" : ""}`}>
                  <div className={`p-2 rounded-xl ${isAnalyticsRoute ? "bg-blue-100" : ""}`}><BarChart3 size={20} /></div>
                  <span className={`${sidebarNavLabelClass} ${isAnalyticsRoute ? "text-blue-600" : ""}`}>Analytics</span>
                </button>
              )}
              <button onClick={() => {
                const currentSessionId = resolvedActiveSession || activeSession ||
                  (typeof window !== 'undefined' ? localStorage.getItem("activeSessionId") : null);
                if (currentSessionId) navigate(`/scribe?sessionId=${currentSessionId}`);
                else navigate("/scribe");
                setMobileMenuOpen(false);
              }}
                className={`${sidebarNavItemClass} ${isVisitsRoute ? "text-blue-600" : ""}`}>
                <div className={`p-2 rounded-xl ${isVisitsRoute ? "bg-blue-100" : ""}`}><Calendar size={20} /></div>
                <span className={`${sidebarNavLabelClass} ${isVisitsRoute ? "text-blue-600" : ""}`}>Visits</span>
              </button>
              {shouldShowFeature("dashboard") && (
                <button onClick={() => { navigate("/scribe/dashboard"); setMobileMenuOpen(false); }}
                  className={`${sidebarNavItemClass} ${isDashboardRoute ? "text-blue-600" : ""}`}>
                  <div className={`p-2 rounded-xl ${isDashboardRoute ? "bg-blue-100" : ""}`}><LayoutDashboard size={20} /></div>
                  <span className={`${sidebarNavLabelClass} ${isDashboardRoute ? "text-blue-600" : ""}`}>Dashboard</span>
                </button>
              )}
              {TEMPLATES_FEATURE_ENABLED && (
                <button onClick={() => {
                  navigate("/scribe/templates"); setSessionVisible(false);
                  setIsHoverLocked(false); setMobileMenuOpen(false);
                }}
                  className={`${sidebarNavItemClass} ${isTemplatesRoute ? "text-blue-600" : ""}`}>
                  <div className={`p-2 rounded-xl ${isTemplatesRoute ? "bg-blue-100" : ""}`}><FileText size={20} /></div>
                  <span className={`${sidebarNavLabelClass} ${isTemplatesRoute ? "text-blue-600" : ""}`}>Templates</span>
                </button>
              )}
              <button onClick={() => {
                navigate("/scribe/settings"); setSessionVisible(false);
                setIsHoverLocked(false); setMobileMenuOpen(false);
              }}
                className={`${sidebarNavItemClass} ${isSettingsRoute ? "text-blue-600" : ""}`}>
                <div className={`p-2 rounded-xl ${isSettingsRoute ? "bg-blue-100" : ""}`}><Settings size={20} /></div>
                <span className={`${sidebarNavLabelClass} ${isSettingsRoute ? "text-blue-600" : ""}`}>Settings</span>
              </button>
              {shouldShowFeature("assign-task") && (
                <button onClick={() => {
                  navigate("/scribe/assign-task"); setSessionVisible(false);
                  setIsHoverLocked(false); setMobileMenuOpen(false);
                }}
                  className={`${sidebarNavItemClass} ${isAssignRoute ? "text-blue-600" : ""} relative`}>
                  <div className={`p-2 rounded-xl ${isAssignRoute ? "bg-blue-100" : ""}`}><ListTodo size={20} /></div>
                  <span className={`${sidebarNavLabelClass} ${isAssignRoute ? "text-blue-600" : ""}`}>Assign</span>
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
              )}
              {shouldShowFeature("upgrade") && (
                <>
                  <div className="border-t border-gray-200 mx-1 pt-1" />
                  <button onClick={() => { setShowSubscriptionModal(true); setMobileMenuOpen(false); }}
                    className={sidebarNavItemClass}>
                    <div className="p-2 rounded-xl"><Rocket size={20} /></div>
                    <span className={sidebarNavLabelClass}>Upgrade</span>
                  </button>
                </>
              )}
              <div ref={helpButtonRef} className="relative help-dropdown">
                <button onClick={() => {
                  if (!showHelpDropdown && helpButtonRef.current) {
                    const rect = helpButtonRef.current.getBoundingClientRect();
                    const dropdownWidth = 220; const padding = 8;
                    let left = rect.right + padding;
                    if (left + dropdownWidth > window.innerWidth - padding) left = rect.left - dropdownWidth - padding;
                    const top = rect.top;
                    setHelpDropdownPosition({ top, left });
                  }
                  setShowHelpDropdown(!showHelpDropdown);
                }}
                  className={sidebarNavItemClass}>
                  <div className="p-2 rounded-xl"><HelpCircle size={20} /></div>
                  <span className={sidebarNavLabelClass}>Help</span>
                </button>
                {showHelpDropdown && createPortal(<HelpPopup />, document.body)}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-3 pt-3 mt-2 border-t border-gray-200 px-2 pb-2">
            {shouldShowFeature("subscription") && <SubscriptionStatusBlock compact />}
            <AvatarMenu
              avatarUrl={avatarUrlFinal}
              initials={avatarInitials}
              isMobile={isMobile}
              showProfilePopup={showProfilePopup}
              setShowProfilePopup={setShowProfilePopup}
              menuItems={profileMenuItems}
              sizeClass="w-10 h-10 text-sm"
            />
          </div>
        </div>

        {/* Right panel — sessions list */}
        <div className="flex-1 min-w-0 flex flex-col bg-[#f8f8fc] overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-gray-200 flex-shrink-0">
            <TooltipProvider delayDuration={0} skipDelayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <button ref={assignNewVisitButtonRef} data-tour="new-visit" data-testid="new-visit-button"
                      onClick={handleCreateSession}
                      disabled={loading || isCreatingSession || (isChartCreationLocked && !isLimitExempt)}
                      className="bg-gradient-to-r from-blue-600 to-teal-500 text-white rounded-2xl px-3 py-2 shadow-sm flex items-center gap-2 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition">
                      {loading || isCreatingSession ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                      ) : (
                        <>
                          {isChartCreationLocked && !isLimitExempt && (
                            <Lock size={14} className="shrink-0 text-white/70" />
                          )}
                          <Plus size={16} />
                        </>
                      )}{" "}New visit
                    </button>
                  </span>
                </TooltipTrigger>
                {chartCreationTooltip ? (
                  <TooltipContent side="bottom" align="start" sideOffset={10}
                    className="max-w-[260px] whitespace-normal shadow-xl backdrop-blur-none"
                    style={{ backgroundColor: "#2b2438", border: "1px solid #4b3f67", color: "#f5f3ff", opacity: 1 }}>
                    {chartCreationTooltip}
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>
            <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-xl hover:bg-gray-100">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-3">
            <SearchFilterRow
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterPopoverOpen={filterPopoverOpen}
              setFilterPopoverOpen={setFilterPopoverOpen}
              filterContent={filterContent}
              isSyncingAudio={isMappingAudio && hasRealSessions}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
            />
            {/* <AudioMappingNotice /> */}

            {shouldShowFeature("subscription") && (
              <div className="mb-4"><SubscriptionStatusBlock /></div>
            )}

            <div ref={onMobileSessionsListRef} onScroll={handleSessionsScroll}
              className="flex-1 min-h-0 overflow-y-auto px-1.5">
              {showAssignedSessions && (
                <>
                  <h2 className="text-sm font-semibold text-gray-700 mb-3">Assigned SOAP Notes</h2>
                  <div className="space-y-3">
                    {sortedAssignedSessions.map((item) => (
                      <AssignedSessionCard key={item.soapNoteId} item={item} />
                    ))}
                  </div>
                </>
              )}

              {showOwnSessions && (
                <>
                  <h2 className="text-sm font-semibold text-gray-700 mb-3">All Visits</h2>
                  <SessionListContent
                    scrollRef={onMobileSessionsListRef}
                    onScroll={handleSessionsScroll}
                    noScrollContainer
                  />
                </>
              )}

              {!displayLoading &&
                !(showAssignedSessions && sortedAssignedSessions.length > 0) &&
                !(showOwnSessions && sortedSessions.length > 0) &&
                (searchTerm || dateFilter !== "all" || creatorFilter !== "all" ||
                  assigneeFilter !== "all" || visitTypeFilter !== "all" || genderFilter !== "all") && (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-sm">No matching visits found</p>
                    <Button variant="outline" size="sm" className="mt-3"
                      onClick={() => {
                        setSearchTerm(""); setDateFilter("all");
                        setCreatorFilter("all"); setAssigneeFilter("all");
                        setVisitTypeFilter("all"); setGenderFilter("all");
                        setCustomDateRange({ from: null, to: null });
                        filtersClearedRef.current = true;
                      }}>Clear all filters</Button>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="h-full bg-[#f7f7fb] flex relative overflow-x-hidden">
      {!isMobile && (
        <div className="w-[96px] xl:w-[112px] bg-white border-r border-gray-200 h-full flex flex-col justify-between gap-3 py-4 xl:py-6 flex-shrink-0 overflow-y-auto overflow-x-hidden">
          <div>
            <div className="flex items-center justify-center mb-8 px-2 w-full min-w-0 cursor-pointer overflow-hidden"
              onClick={() => {
                const currentSessionId = resolvedActiveSession || activeSession;
                navigate(currentSessionId ? `/scribe?sessionId=${currentSessionId}` : "/scribe");
              }}>
              <img src={logo} alt="VirtualScribe" className="h-6 w-auto object-contain" />
            </div>
            <div className="space-y-3 xl:space-y-5 px-2">
              {shouldShowFeature("analytics") && (
                <div>
                  <button onClick={() => {
                    navigate({ pathname: "/admin/analytics", search: "" });
                    setSessionVisible(false); setIsHoverLocked(false);
                  }}
                    className={`${sidebarNavItemClass} ${isAnalyticsRoute ? "text-blue-600" : ""}`}>
                    <div className={`p-2 rounded-xl ${isAnalyticsRoute ? "bg-blue-100" : ""}`}><BarChart3 size={22} /></div>
                    <span className={`${sidebarNavLabelClass} ${isAnalyticsRoute ? "text-blue-600" : ""}`}>Analytics</span>
                  </button>
                </div>
              )}
              <div>
                <button onClick={() => {
                  handleVisitsClick();
                  const currentSessionId = resolvedActiveSession || activeSession ||
                    (typeof window !== "undefined" ? localStorage.getItem("activeSessionId") : null);
                  if (currentSessionId) navigate(`/scribe?sessionId=${currentSessionId}`);
                  else navigate("/scribe");
                }}
                  className={`${sidebarNavItemClass} ${isVisitsRoute ? "text-blue-600" : ""}`}>
                  <div className={`p-2 rounded-xl ${isVisitsRoute ? "bg-blue-100" : ""}`}><Calendar size={22} /></div>
                  <span className={`${sidebarNavLabelClass} ${isVisitsRoute ? "text-blue-600" : ""}`}>Visits</span>
                </button>
              </div>
              {shouldShowFeature("dashboard") && (
                <button onClick={() => { navigate("/scribe/dashboard"); setMobileMenuOpen(false); }}
                  className={`${sidebarNavItemClass} ${isDashboardRoute ? "text-blue-600" : ""}`}>
                  <div className={`p-2 rounded-xl ${isDashboardRoute ? "bg-blue-100" : ""}`}><LayoutDashboard size={22} /></div>
                  <span className={`${sidebarNavLabelClass} ${isDashboardRoute ? "text-blue-600" : ""}`}>Dashboard</span>
                </button>
              )}
              {TEMPLATES_FEATURE_ENABLED && (
                <button onClick={() => {
                  navigate("/scribe/templates"); setSessionVisible(false); setIsHoverLocked(false);
                }}
                  className={`${sidebarNavItemClass} ${isTemplatesRoute ? "text-blue-600" : ""}`}>
                  <div className={`p-2 rounded-xl ${isTemplatesRoute ? "bg-blue-100" : ""}`}><FileText size={22} /></div>
                  <span className={`${sidebarNavLabelClass} ${isTemplatesRoute ? "text-blue-600" : ""}`}>Templates</span>
                </button>
              )}
              <button onClick={() => {
                navigate("/scribe/settings"); setSessionVisible(false); setIsHoverLocked(false);
              }}
                className={`${sidebarNavItemClass} ${isSettingsRoute ? "text-blue-600" : ""}`}>
                <div className={`p-2 rounded-xl ${isSettingsRoute ? "bg-blue-100" : ""}`}><Settings size={22} /></div>
                <span className={`${sidebarNavLabelClass} ${isSettingsRoute ? "text-blue-600" : ""}`}>Settings</span>
              </button>
              {shouldShowFeature("assign-task") && (
                <button onClick={() => {
                  navigate("/scribe/assign-task"); setSessionVisible(false); setIsHoverLocked(false);
                }}
                  className={`${sidebarNavItemClass} ${isAssignRoute ? "text-blue-600" : ""} relative`}>
                  <div className={`p-2 rounded-xl ${isAssignRoute ? "bg-blue-100" : ""}`}><ListTodo size={22} /></div>
                  <span className={`${sidebarNavLabelClass} ${isAssignRoute ? "text-blue-600" : ""}`}>Assign</span>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
              )}
              {shouldShowFeature("upgrade") && (
                <>
                  <div className="border-t border-gray-200 mx-1 pt-1" />
                  <button onClick={() => setShowSubscriptionModal(true)} className={sidebarNavItemClass}>
                    <div className="p-2 rounded-xl"><Rocket size={22} /></div>
                    <span className={sidebarNavLabelClass}>Upgrade</span>
                  </button>
                </>
              )}
              <div ref={helpButtonRef} className="relative help-dropdown">
                <button onClick={() => {
                  if (!showHelpDropdown && helpButtonRef.current) {
                    const rect = helpButtonRef.current.getBoundingClientRect();
                    const dropdownWidth = 220; const padding = 8;
                    let left = rect.right + padding;
                    if (left + dropdownWidth > window.innerWidth - padding) left = rect.left - dropdownWidth - padding;
                    const top = rect.top;
                    setHelpDropdownPosition({ top, left });
                  }
                  setShowHelpDropdown(!showHelpDropdown);
                }} className={sidebarNavItemClass}>
                  <div className="p-2 rounded-xl"><HelpCircle size={22} /></div>
                  <span className={sidebarNavLabelClass}>Help</span>
                </button>
                {showHelpDropdown && createPortal(<HelpPopup />, document.body)}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 pt-3 mt-2 border-t border-gray-200 px-2">
            {shouldShowFeature("subscription") && <SubscriptionStatusBlock compact />}
            <div className="w-full px-0">
              <AccessibilityWidget className="w-full" />
            </div>
            <AvatarMenu
              avatarUrl={avatarUrlFinal}
              initials={avatarInitials}
              isMobile={isMobile}
              showProfilePopup={showProfilePopup}
              setShowProfilePopup={setShowProfilePopup}
              menuItems={profileMenuItems}
              sizeClass="w-14 h-14 text-xl"
            />
          </div>
        </div>
      )}

      {!isMobile && desktopLayout}
      {isMobile && mobileLayout}

      {deleteConfirm && shouldShowFeature("delete-session") && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold">Delete Session</h3>
            </div>
            <p className="text-gray-600 mb-6">
              This session will be permanently deleted and cannot be recovered.
            </p>
            <div className="flex justify-end gap-3">
              <Button onClick={cancelDelete} variant="outline" disabled={isDeletingSession}>Cancel</Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={isDeletingSession}
              >
                {isDeletingSession ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Deleting…
                  </span>
                ) : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {bulkDeleteConfirm && shouldShowFeature("delete-session") && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold">Delete {bulkDeleteConfirm.length} session(s)</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {bulkDeleteConfirm.length === 1
                ? "This session will be permanently deleted and cannot be recovered."
                : `These ${bulkDeleteConfirm.length} sessions will be permanently deleted and cannot be recovered.`}
            </p>
            <div className="flex justify-end gap-3">
              <Button onClick={() => setBulkDeleteConfirm(null)} variant="outline" disabled={isBulkDeleting}>Cancel</Button>
              <Button
                onClick={confirmBulkDelete}
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Deleting…
                  </span>
                ) : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {shouldShowFeature("upgrade") && (
        <VBotSubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          onSubscribe={() => setShowSubscriptionModal(false)}
          onContactSales={goToTalkToSales}
          subscriptionInfo={{
            planType: trialStatus.planType,
            subscriptionEnd: trialStatus.subscriptionEnd,
            subscriptionStart: trialStatus.subscriptionStart,
            subscriptionDaysRemaining: trialStatus.subscriptionDaysRemaining,
            billingPeriod: trialStatus.billingPeriod,
            billingPeriodLabel: trialStatus.billingPeriodLabel,
            planKey: trialStatus.planKey,
            seatCount: trialStatus.seatCount,
          }}
          onRefreshSubscription={refreshTrialStatus}
        />
      )}
    </div>
  );
}

export default memo(SessionManager);